import maplibregl from "maplibre-gl";
import type { MapGeoJSONFeature } from "maplibre-gl";
import {
  indexRoads,
  isRoadLayer,
  simplifyToMax,
  type RoadSpatialIndex,
} from "../shared/editor-geom.ts";
import { DraftScene, mergeDraftDiffs, type DraftDiff, type DraftInput } from "../shared/draft-scene.ts";
import {
  applyStylePatch,
  cloneLineStyle,
  cloneLineStyles,
  clonePointStyle,
  clonePointStyles,
  collectStyleTargets,
  copySelectionStyles,
  defaultStyleDefaults,
  dissolvedLineStyles,
  joinedLineStyles,
  matchPointsForStyles,
  normalizeLineStyle,
  normalizePointStyle,
  packLineStyles,
  packPointStyles,
  parseStyleDefaults,
  pasteSelectionStyles,
  remapLineStyles,
  remapPointStyles,
  sameLineStyle,
  sameLineStyleList,
  samePointStyle,
  samePointStyleList,
  styleFormValues,
  type LineStyle,
  type PointStyle,
  type StyleClipboard,
  type StyleDefaults,
  type StyleFormValues,
  type StyleTargets,
} from "../shared/feature-style.ts";
import {
  cloneDecorative,
  decorativeFormValues,
  dissolvedDecorative,
  joinedDecorative,
  packDecorative,
  remapDecorative,
  toggleDecorativeOnLines,
} from "../shared/decorative-segments.ts";
import {
  DASHED_LINE_FILTER,
  DECORATIVE_LINE_FILTER,
  DOTTED_LINE_FILTER,
  DRAFT_LINE_COLOR,
  DRAFT_LINE_OPACITY,
  DRAFT_LINE_WIDTH,
  EDITOR_DECORATIVE_HIT_FILTER,
  EDITOR_DECORATIVE_HIT_WIDTH,
  EDITOR_DECORATIVE_LINE_OPACITY,
  ROUTE_LINE_LAYOUT,
  ROUTE_LINE_SHADOW_PAINT,
  SOLID_LINE_FILTER,
} from "../shared/line-paint.ts";
import { routeHomeMapFitPoints, type RouteFillFeature, type RoutePaintFeature } from "../shared/quest-route-paint.ts";
import {
  computeRoute,
  graphFromLine,
  sanitizeGraph,
  splitSegment,
  type RouteResult,
} from "../shared/route-graph.ts";
import { haversineM } from "../shared/geo.ts";
import {
  routeConnectivityParts,
  crossingSpots,
  findRouteIssues,
  overlapSpots,
  routeIssueFeatures,
  smallerPartBounds,
  smallerPartPoints,
  type ConnectedParts,
  type RouteIssues,
} from "../shared/route-issues.ts";
import {
  COINCIDE_TOLERANCE_M,
  DEFAULT_SPACING_M,
  JUNCTION_TOLERANCE_M,
  MAX_FILL_AREA_M2,
  MAX_FILL_INPUT_VERTICES,
  MAX_FILL_POINTS,
  clampSpacing,
  clipLinesToPolygon,
  clipLinesToStroke,
  endsNear,
  eraseSegments,
  exciseWeldedEdges,
  mergeGraphs,
  polygonAreaM2,
  resampleRoadGraph,
  subtractFilledRoads,
  weldRoadLines,
} from "../shared/road-fill.ts";
import { readShowPoints, writeShowPoints } from "../shared/point-markers.ts";
import {
  ERASE_RADIUS_DEFAULT,
  clampEraseRadius,
  eraseWheelPixels,
  readEraseRadius,
  resizeEraseRadius,
  writeEraseRadius,
} from "../shared/erase-brush.ts";
import {
  SELECT_CIRCLE_DEFAULT,
  nextSelectTool,
  readSelectCircleRadius,
  readSelectTool,
  beginSelectGesture,
  emptyClickClears,
  selectPressRelease,
  selectToolIsolates,
  resizeSelectCircleRadius,
  writeSelectCircleRadius,
  writeSelectTool,
  type SelectTool,
} from "../shared/select-tool.ts";
import {
  nextRoadsTool,
  readRoadsBrushRadius,
  readRoadsTool,
  resizeRoadsBrushRadius,
  ROADS_BRUSH_DEFAULT,
  screenRectCorners,
  screenRectReady,
  writeRoadsBrushRadius,
  writeRoadsTool,
  type RoadsTool,
} from "../shared/roads-tool.ts";
import {
  applyLasso,
  deleteSelection,
  pointInRing,
  remapSelection,
  segmentsHittingRing,
  type LassoMode,
} from "../shared/lasso.ts";
import {
  applyClick,
  cycleSelectAll,
  lineHighlightFor,
  selectableHit,
  type Pick,
} from "../shared/selection.ts";
import { centroid, chainOrder, joinSegments, mergePoints } from "../shared/join-merge.ts";
import { applyViewRotate, MAX_PITCH } from "../shared/view-rotate.ts";
import type { NorthMemory, NorthView } from "../shared/north-toggle.ts";
import { compassStart, compassToggle, compassVisible, type CompassStep } from "../shared/quest-compass.ts";
import { DASHED_PATTERN, DOTTED_PATTERN } from "../shared/route-line-scale.ts";
import type { Insets, ScreenRect } from "../shared/rotated-fit.ts";
import { CameraZoomControl } from "./home/camera-zoom-control.ts";
import { MyLocationControl } from "./home/my-location-control.ts";
import { UserLocationMark } from "./home/user-location-mark.ts";
import { MapCamera, QUEST_VIEW_MS, type SavedMapView } from "./home/map-camera.ts";
import { QuestHighlight } from "./home/quest-highlight.ts";
import { HomeQuestSpots, type HomeSpotPin } from "./home/home-quest-spots.ts";
import {
  NEARBY_CLUSTER,
  NEARBY_LAYER,
  HOME_QUEST_ROUTE_IDS,
  NEARBY_SOURCE,
  ensureCachedRouteLines,
  ensureNearbyLayers,
  paintNearbyHighlight,
  setCachedRouteLines,
  setHomeQuestRoute,
  setNearbyPins,
  type CachedRouteLine,
  type NearbyPin,
} from "./home/home-map-layers.ts";
import { deferAfterMapFrame, finiteMapCenter } from "../shared/map-frame.ts";
import { mapHasLayer, mapHasStyleObject, mapStyleReady, whenMapHasStyle } from "../shared/map-style-safe.ts";
import { VECTOR_STYLE_URL, vectorAttributionHtml } from "../shared/basemap.ts";
import { MapBasemap } from "./basemap.ts";
import { MapViewControls } from "./map-view-controls.ts";
import { wheelTargetsMapOverlay } from "./map-overlay-wheel.ts";
import {
  cloneRegions,
  faceAt,
  facesShareFill,
  fillCursor,
  NO_FILL,
  paintAllFaces,
  REGION_FILL_ANTIALIAS,
  RegionLayerSync,
  normalizeColor,
  planarFaces,
  reconcileFills,
  refreshFaceBounds,
  sameRegions,
  segmentSignature,
  type PlanarFace,
  type RegionFill,
} from "../shared/region-fill.ts";
import { canvasCursor, type ViewCursor } from "../shared/map-cursor.ts";
import {
  commitGrab,
  grabAnchor,
  grabChanged,
  grabDelta,
  grabTargets,
  moveGrabTo,
} from "../shared/grab-move.ts";
import {
  deleteMenuEnabled,
  linesForDelete,
  pointsForDelete,
  type DeleteMenuChoice,
} from "../shared/delete-menu.ts";
import { dissolvePoints } from "../shared/dissolve.ts";
import { applyCirclePaint } from "../shared/circle-select.ts";
import {
  buildScreenHitGrid,
  pointsUnderBrush,
  segmentsUnderBrush,
  type ScreenHitGrid,
  type ScreenPoint,
} from "../shared/screen-hit.ts";
import { beginEraseStroke, consumeEraseHits, eraseTargets, removedSegmentIndexes, type EraseStrokeState } from "../shared/erase-stroke.ts";
import { deleteIsHeavy, eraseCommitIsHeavy, fillIsHeavy, roadExtractIsHeavy } from "../shared/heavy-work.ts";
import { yieldForPaint } from "./wait-cursor.ts";
import type { LngLat, PieceGraph, WorkArea } from "../shared/types.ts";
import {
  canvasBearing,
  canvasBounds,
  canvasCenter,
  canvasCorners,
  canvasHandleLocals,
  canvasHalfSizeM,
  clipLinesToWorkArea,
  fromLocalM,
  idleExtractWatch,
  normalizeWorkArea,
  pointInWorkArea,
  resizeCanvas,
  rotateCanvas,
  boundsOverlap,
  roadTileZoom,
  serializeWorkArea,
  shouldExtractRoads,
  shouldUpgradeRoads,
  toLocalM,
  translateCanvas,
  unionRoads,
  watchAfterExtract,
  watchForNewArea,
  type CanvasHandle,
  type ExtractReason,
  type ExtractWatch,
} from "../shared/work-area.ts";
import { roadSnapPoint } from "../shared/map-magnet.ts";
import { readSnapPref, writeSnapPref } from "../shared/snap-pref.ts";
import { incidentMask, joinDroppedPoint, pickSnapTarget, type SnapTarget } from "../shared/vertex-snap.ts";
import { t } from "./i18n/index.ts";
import {
  actionMatchesPointer,
  actionMatchesWheel,
  actionUsesButton,
  blocksEditing,
  createKeymap,
  detectPlatform,
  heldView,
  matchPointer,
  matchWheel,
  type Keymap,
  type ModifierState,
  type Platform,
  type PointerState,
} from "./input/keymap.ts";
import type { ActionId } from "./input/actions.ts";

/** OpenFreeMap Bright — better foot/path contrast than Liberty (no API key). */
export const STYLE_URL = VECTOR_STYLE_URL;
export const DEFAULT_CENTER: LngLat = [126.978, 37.5665];
export const DEFAULT_ZOOM = 13;
export const MIN_EDIT_ZOOM = 15;
const COMPASS_TURN_MS = 400;
const COMPASS_LABEL: Record<CompassStep["kind"], "rt.map.compassNorth" | "rt.map.compassBack" | "rt.map.compassRoute"> = {
  north: "rt.map.compassNorth",
  restore: "rt.map.compassBack",
  canvas: "rt.map.compassRoute",
  none: "rt.map.compassNorth",
};

const DRAFT_SOURCE = "draft";
const DRAFT_LINE = "draft-line";
const DRAFT_LINE_SHADOW = "draft-line-shadow";
const DRAFT_LINE_SHADOW_DASH = "draft-line-shadow-dash";
const DRAFT_LINE_SHADOW_DOT = "draft-line-shadow-dot";
const DRAFT_LINE_DASH = "draft-line-dash";
const DRAFT_LINE_DOT = "draft-line-dot";
const DRAFT_LINE_DECOR = "draft-line-decor";
const DRAFT_LINE_DECOR_HIT = "draft-line-decor-hit";
const DRAFT_PICK = "draft-pick";
const DRAFT_POINTS = "draft-points";
const ISSUE_SOURCE = "route-issues";
const ISSUE_OVERLAP = "route-issue-overlap";
const ISSUE_STRAY = "route-issue-stray";
const ISSUE_GAP_LINK = "route-issue-gap-link";
const ISSUE_MARKS = "route-issue-marks";
/** Quiet time after an edit before the drawing is checked again. */
const ISSUE_DELAY_MS = 150;
const BOUNDARY_SOURCE = "boundary";
const BOUNDARY_FILL = "boundary-fill";
const BOUNDARY_LINE = "boundary-line";
const BOUNDARY_POINTS = "boundary-points";
const WORK_SOURCE = "work-area";
const WORK_DIM = "work-area-dim";
const WORK_LINE = "work-area-line";
const WORK_HANDLES = "work-area-handles";
const WORK_ROTATE_ARM = "work-area-rotate-arm";
const CANVAS_HANDLE_HIT_PX = 14;
const REGION_SOURCE = "region-fills";
const REGION_FILL = "region-fill";
const HISTORY_LIMIT = 80;
/** First boundary vertex uses this generous hit so a near click can close it. */
const BOUNDARY_CLOSE_PX = 44;
/** Matches MapLibre's click tolerance so a twitch is still a click, and anything past it is a lasso. */
const LASSO_DRAG_PX = 3;
/** A shorter drag is a click, not a new work area. */
const WORK_DRAG_PX = 8;

type InteractionMode = "graph" | "boundary" | "erase" | "work" | "fill" | "roadsBox" | "roadsCircle";

interface FillMemory {
  basePoints: LngLat[];
  welded: PieceGraph;
  resampled: PieceGraph;
  covers: number[][];
}

interface Snapshot {
  /** Canvas frame, so moving, resizing, rotating, or clearing it can be undone. */
  workArea: WorkArea | null;
  points: LngLat[];
  segments: Array<[number, number]>;
  start: number | null;
  selection: number[];
  selectedLines: number[];
  /** Click order. Null when the selection came from lasso, box, circle, or select-all. */
  clickOrder: number[] | null;
  /** First selected point, used when click order is unknown. */
  orderAnchor: number | null;
  spacing: number;
  fill: FillMemory | null;
  regions: RegionFill[];
  pointStyles: Array<PointStyle | null>;
  lineStyles: Array<LineStyle | null>;
  decorative: boolean[];
  styleDefaults: StyleDefaults;
  pieceStyleLocked: boolean;
}

interface VertexDrag {
  pointerId: number;
  index: number;
  x: number;
  y: number;
  before: Snapshot;
  moved: boolean;
  /** Mouse and pen select. Touch still connects, the way a tap always has. */
  fine: boolean;
  /** Shift-click (or a rebound toggle) adds. A plain select replaces. */
  additive: boolean;
  /** This press may change the selection when it does not move. */
  picks: boolean;
  /** False when Move Point is bound to another button, so a click stays a click. */
  moves: boolean;
  /** Other points and lines as they were on screen when the drag began. */
  snapGrid?: { grid: ScreenHitGrid; points: ScreenPoint[]; hidden: Uint8Array } | null;
  /** Point or line the vertex will join on drop. */
  snap?: SnapTarget | null;
}

interface SegmentDrag {
  pointerId: number;
  segment: number;
  x: number;
  y: number;
  moved: boolean;
  additive: boolean;
}

interface BoundaryDrag {
  pointerId: number;
  index: number;
  x: number;
  y: number;
  moved: boolean;
}

interface LassoDrag {
  pointerId: number;
  x: number;
  y: number;
  moved: boolean;
  /** Null when this empty-map press is only a click, not a lasso binding. */
  mode: LassoMode | null;
  ring: Array<{ x: number; y: number }>;
  additive: boolean;
  /** A select click on empty map clears. A toggle click does not. */
  clears: boolean;
  /** Shape captured when the drag started. W during the drag does not change it. */
  tool: SelectTool;
  /** Circle select remembers indexes so each sample paints only new hits. */
  hits: number[];
  hitMark: Uint8Array | null;
  lineMark: Uint8Array | null;
  /** Replace has already cleared the selection for this circle stroke. */
  replaced: boolean;
}

interface ViewDrag {
  pointerId: number;
  action: "panView" | "rotateView";
  x: number;
  y: number;
  lastX: number;
  lastY: number;
  moved: boolean;
  /** Manual transform nudge allowed after programmatic motion has stopped. */
  nudgeOk: boolean;
  /** True once {@link nudgeView} actually moved the transform this gesture. */
  nudged: boolean;
}

interface MoveSample {
  pointerId: number;
  clientX: number;
  clientY: number;
  alt: boolean;
  shift: boolean;
  ctrl: boolean;
  meta: boolean;
  overMap: boolean;
}

interface EraseDrag {
  pointerId: number;
  before: Snapshot;
  erased: boolean;
  x: number;
  y: number;
  /** Hits stay on the original indexes until the pointer is released. */
  stroke: EraseStrokeState;
}

interface WorkDrag {
  pointerId: number;
  x: number;
  y: number;
  origin: { x: number; y: number };
  moved: boolean;
  /** Map bearing when the drag started; canvas stays screen-aligned. */
  bearing: number;
}

interface CanvasEditDrag {
  pointerId: number;
  handle: CanvasHandle;
  start: WorkArea;
  origin: { x: number; y: number };
  /** Screen position of the canvas center at drag start (rotate). */
  centerScreen: { x: number; y: number };
  startAngle: number;
  startBearing: number;
  /** Unprojected pointer at drag start (move). */
  originLl: LngLat;
  moved: boolean;
}

interface RoadsBoxDrag {
  pointerId: number;
  origin: { x: number; y: number };
  x: number;
  y: number;
  moved: boolean;
}

interface RoadsCircleDrag {
  pointerId: number;
  samples: LngLat[];
  /** Road pieces already under the brush, drawn live while dragging. */
  preview?: LngLat[][];
  lines?: LngLat[][];
  x: number;
  y: number;
}

/** Blender-style Grab. Positions stay at `origins` until confirm or cancel. */
interface GrabSession {
  indices: number[];
  /** Grabbed point nearest the cursor when the move started. It is the one that snaps. */
  anchor: number;
  origins: LngLat[];
  origin: LngLat;
  before: Snapshot;
}

/** Desktop segment grab. Touch taps use a wider radius in segmentHitPx. */
const SEGMENT_HIT_FINE_PX = 24;
const SEGMENT_HIT_TOUCH_PX = 36;

/** Kinds of drawing problems the stats bar can count and zoom to. */
export type RouteIssueKind = "part" | "gap" | "overlap" | "crossing";

export interface RouteIssueCounts {
  gaps: number;
  overlaps: number;
  crossings: number;
}

export class MapEditor {
  readonly map: maplibregl.Map;
  private readonly onChange: (edited: boolean) => void;
  private readonly onNearbyClick: (id: string) => void;
  /** Home browse: empty map click (not a pin or selected route). */
  onBrowseMapBackgroundClick: (() => void) | null = null;
  /** Home browse: user finished panning/rotating via transform nudge (no moveend until snap). */
  onBrowseMapUserChanged: (() => void) | null = null;
  /** Home browse: after the camera finishes easing to the user's location. */
  onMyLocationArrived: (() => void) | null = null;
  /** Home browse: spot checkpoint picked on the background map. */
  onHomeQuestSpotSelect: ((index: number) => void) | null = null;
  /** Reset programmatic move counter when a quest selection fit starts (live verify). */
  onHomeQuestFitStart: (() => void) | null = null;
  /** After leaving edit back to browse, once the saved camera (if any) has been restored. */
  onRestoreBrowseAfterEdit: (() => void) | null = null;
  private readonly onStatus: (message: string) => void;
  private readonly surface: HTMLElement;

  /** Snap to roads and to other points. Remembered across sessions; starts on. */
  private magnetOn = true;
  /** View the last North press left, so the next press can turn back. */
  private northMemory: NorthMemory | null = null;
  /** Camera a North ease is heading to, until it settles. */
  private northTarget: NorthView | null = null;
  /** Route of the quest selected on the home map, framed by its start view. Null: none selected. */
  private questViewRoute: LngLat[] | null = null;
  private questViewSpots: HomeSpotPin[] | null = null;
  /** Route/marker DOM waits until the quest ease has scheduled its first frame. */
  private questLayersDeferred = false;
  private questLayerFlushGen = 0;
  private homeQuestSpotSelectedIndex: number | null = null;
  private questRoutePaint: readonly RoutePaintFeature[] | null = null;
  private questRouteFill: readonly RouteFillFeature[] | null = null;
  private questRouteOnMap = false;
  /** Bearing the selected quest's route canvas reads upright at; 0 with none (North only). */
  private questViewBearing = 0;
  /** Drops stale home route / highlight paints after quest selection or style idle callbacks. */
  private homeMapLayerGen = 0;
  /** Every programmatic camera move (quest framing, list-card centering, keyboard, edit fits). */
  readonly camera: MapCamera;
  /** Home browse view saved when entering edit; restored on exit (see setBrowseMode). */
  private browseViewBeforeEdit: (SavedMapView & { framed: boolean }) | null = null;
  /** Skip one canvas-resize quest refit right after restoring the pre-edit browse camera. */
  private skipNextQuestResizeRefit = false;
  /** Skip quest refits from canvas remeasure while a selection fit is scheduled or animating. */
  private suppressQuestResizeRefit = false;
  private questStartFitGen = 0;
  private readonly highlight: QuestHighlight;
  private readonly homeQuestSpots: HomeQuestSpots;
  private viewControls: MapViewControls;
  private myLocationControl: MyLocationControl;
  private userLocation: UserLocationMark;
  private locating = false;
  private basemap: MapBasemap;
  /** Called on a compass press (main.ts routes it through the North action). */
  onCompassPress: (() => void) | null = null;
  private crosshairOn = false;
  private showPoints = true;
  private eraseRadius = ERASE_RADIUS_DEFAULT;
  private selectionTool: SelectTool = "off";
  private selectCircleRadius = SELECT_CIRCLE_DEFAULT;
  private roadsTool: RoadsTool = "box";
  private roadsBrushRadius = ROADS_BRUSH_DEFAULT;
  /** True while the idle circle cursor is on the overlay, so other tools do not clear every move. */
  private selectCircleShown = false;
  /** True while a drag's snap ring is on the overlay. */
  private snapMarkShown = false;
  private points: LngLat[] = [];
  private segments: Array<[number, number]> = [];
  /** Parallel to points. Null keeps the role color and size. */
  private pointStyles: Array<PointStyle | null> = [];
  /** Parallel to segments. Null keeps the road-band color and a solid stroke. */
  private lineStyles: Array<LineStyle | null> = [];
  /** Parallel to segments. Decorative lines (장식선) close fills but are not walked. */
  private decorative: boolean[] = [];
  /** New segments are decorative until this mode is turned off. */
  private decorativeStroke = false;
  private nextStyleDefaults: StyleDefaults = defaultStyleDefaults();
  private settingsStyleDefaults: StyleDefaults = defaultStyleDefaults();
  /** True after this piece stores or pastes its own default. */
  private pieceStyleLocked = false;
  /** View mode: map and drawing only. Editing overlays and shortcuts stay off. */
  private browseMode = true;
  /** Null until the creator picks a start. The run then uses the default. */
  private start: number | null = null;
  private past: Snapshot[] = [];
  private future: Snapshot[] = [];
  private selection: number[] = [];
  /** Lines picked by a click. Separate from lines whose endpoints are both selected. */
  private selectedLines: number[] = [];
  /** Bumps when the user changes selection outside the select-all cycle. */
  private selectionRevision = 0;
  /** Revision when A last applied a cycle step; mismatch restarts at all. */
  private selectAllCycleRevision = -1;
  private selectAllCyclePhase: 1 | 2 | 3 = 1;
  /** Order points were clicked. Null after a lasso, box, circle, or select-all. */
  private clickOrder: number[] | null = null;
  /** Where a nearest-neighbor chain starts when click order is unknown. */
  private orderAnchor: number | null = null;
  /** Old-to-new point map for the paint that follows a merge. */
  private pendingPointMap: Int32Array | null = null;
  /** Click or chain order for a join, so each new segment can inherit a neighbor's style. */
  private pendingJoinOrder: number[] | null = null;
  /** Old segment indexes collapsed into each dissolve bridge. Empty entries survived. */
  private pendingCollapse: number[][] | null = null;
  private ready = false;
  private roads: Array<[LngLat, LngLat]> = [];
  private roadIndex: RoadSpatialIndex | null = null;
  /** Per-segment road colors and the feature diff published to the draft source. */
  private scene = new DraftScene();
  /** One rectangle per piece. Roads inside it are read once. See ROAD_EXTRACT_ZOOM. */
  private workArea: WorkArea | null = null;
  private workPreview: WorkArea | null = null;
  private extractWatch: ExtractWatch = idleExtractWatch();
  /** Road tile zoom the stored roads were read from. -1 before the first read. */
  private roadReadZoom = -1;
  private extractTimer = 0;
  private roadSourceIds = new Set<string>();
  private offRoad = false;
  private ignoreClick = false;
  private routeTick = 0;
  private routeCache: { tick: number; result: RouteResult } | null = null;
  private partsCache: { tick: number; parts: ConnectedParts } | null = null;
  /** Last drawing check. Stale while an edit waits out ISSUE_DELAY_MS. */
  private issues: RouteIssues | null = null;
  private issueSpots: { gap: Array<[LngLat, LngLat]>; overlap: LngLat[]; crossing: LngLat[] } = {
    gap: [],
    overlap: [],
    crossing: [],
  };
  private issuesTick = -1;
  private issuesPendingTick = -1;
  private issuesTimer = 0;

  private vertexDrag: VertexDrag | null = null;
  private segmentDrag: SegmentDrag | null = null;
  private boundaryDrag: BoundaryDrag | null = null;
  private eraseDrag: EraseDrag | null = null;
  private viewDrag: ViewDrag | null = null;
  private lassoDrag: LassoDrag | null = null;
  private workDrag: WorkDrag | null = null;
  private canvasEditDrag: CanvasEditDrag | null = null;
  private roadsBoxDrag: RoadsBoxDrag | null = null;
  private roadsCircleDrag: RoadsCircleDrag | null = null;
  private grab: GrabSession | null = null;
  /** While the delete menu is open, map clicks must not select or place. */
  private deleteMenuOpen = false;
  private onDeleteMenuDismiss: (() => void) | null = null;
  /** Right-click (or a rebound click) that places a point when it does not drag. */
  private placeClick: { pointerId: number; x: number; y: number; moved: boolean } | null = null;
  /** Last pointer over the map, in canvas pixels. Key bindings place here. */
  private cursor: { x: number; y: number } | null = null;
  /** Last pointer in viewport pixels, so the delete menu can open under it. */
  private pointerAt: { x: number; y: number } | null = null;
  private pointerOverMap = false;
  private mods: ModifierState = { alt: false, shift: false, ctrl: false, meta: false };
  /** Last value written to the canvas. Unchanged cursors skip the style write. */
  private appliedCursor: string | null = null;
  /** Heavy work in progress. Pointer edits wait until the wait cursor is done. */
  private blocking = 0;
  /** A road extract is already waiting to query, so a second tile event does not start another. */
  private extracting = false;
  private overNearby = false;
  /** Projected points and segments. Dropped when the camera or the graph changes. */
  private screenGrid: ScreenHitGrid | null = null;
  private canvasRect = { left: 0, top: 0, width: 0, height: 0 };
  private readonly overlay: HTMLCanvasElement;
  private readonly overlayCtx: CanvasRenderingContext2D | null;
  private moveQueued = false;
  private moveSample: MoveSample | null = null;
  private keymap: Keymap = createKeymap();
  private inputPlatform: Platform = "other";
  private inputSuspended = false;
  private spacingGesture: Snapshot | null = null;

  private mode: InteractionMode = "graph";
  /** Painted faces. Identity is the boundary's point ids. */
  private regions: RegionFill[] = [];
  private regionColor = "#3b82f6";
  /** Last copied point and line style. Empty until a selection is copied. */
  private styleClipboard: StyleClipboard | null = null;
  private regionFaces: PlanarFace[] = [];
  private regionSig = 0;
  private regionKnown = false;
  private regionLayer = new RegionLayerSync();
  private regionIdle: number | null = null;
  /** True while the palette is open, so a click outside does not fill. */
  private fillPaletteOpen = false;
  private boundary: LngLat[] = [];
  private boundaryClosed = false;
  private spacing = DEFAULT_SPACING_M;
  private fill: FillMemory | null = null;

  constructor(
    container: HTMLElement,
    onChange: (edited: boolean) => void,
    onNearbyClick: (id: string) => void,
    onStatus: (message: string) => void,
  ) {
    this.onChange = onChange;
    this.onNearbyClick = onNearbyClick;
    this.onStatus = onStatus;
    try {
      this.magnetOn = readSnapPref(localStorage);
      this.showPoints = readShowPoints(localStorage);
      this.eraseRadius = readEraseRadius(localStorage);
      this.selectionTool = readSelectTool(localStorage);
      this.selectCircleRadius = readSelectCircleRadius(localStorage);
      this.roadsTool = readRoadsTool(localStorage);
      this.roadsBrushRadius = readRoadsBrushRadius(localStorage);
    } catch {
      this.showPoints = true;
      this.eraseRadius = ERASE_RADIUS_DEFAULT;
      this.selectionTool = "off";
      this.selectCircleRadius = SELECT_CIRCLE_DEFAULT;
      this.roadsTool = "box";
      this.roadsBrushRadius = ROADS_BRUSH_DEFAULT;
    }
    this.map = new maplibregl.Map({
      container,
      style: STYLE_URL,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: false,
      // Right-click places a point. Rotate and pitch stay on Alt+Shift-drag.
      // Alt+Shift-drag turns bearing and tilts pitch, up to this cap.
      dragRotate: false,
      pitchWithRotate: false,
      maxPitch: MAX_PITCH,
      // Shift-drag is lasso-add. Box zoom would steal that gesture.
      boxZoom: false,
    });
    this.map.doubleClickZoom.disable();
    this.camera = new MapCamera(this.map);
    this.camera.setBrowseGestureActive(() => this.viewDrag != null);
    this.highlight = new QuestHighlight(this.map, (id) => {
      if (!this.ready || !mapStyleReady(this.map)) return;
      try {
        paintNearbyHighlight(this.map, id);
      } catch {
        /* MapLibre must keep rendering if highlight paint fails mid-frame */
      }
    });
    this.homeQuestSpots = new HomeQuestSpots(this.map);
    this.suppressMousePan();
    // Arrow keys and +/- are keymap actions, so the library handler stays off.
    this.map.keyboard.disable();
    this.map.touchZoomRotate.disable();
    this.surface = this.map.getCanvasContainer();
    this.overlay = document.createElement("canvas");
    this.overlay.className = "gesture-overlay";
    this.overlay.setAttribute("aria-hidden", "true");
    this.map.getContainer().appendChild(this.overlay);
    this.overlayCtx = this.overlay.getContext("2d");
    const blockMenu = (event: Event) => event.preventDefault();
    this.map.getCanvas().addEventListener("contextmenu", blockMenu);
    this.surface.addEventListener("contextmenu", blockMenu);
    const credit = document.createElement("div");
    credit.className = "home-map-credit route-map-credit";
    credit.innerHTML = vectorAttributionHtml();
    this.map.getContainer().appendChild(credit);

    this.map.addControl(new CameraZoomControl(this.camera), "bottom-right");
    this.userLocation = new UserLocationMark(this.map);
    this.myLocationControl = new MyLocationControl(() => this.goToMyLocation());
    this.map.addControl(this.myLocationControl, "bottom-right");
    // Bottom corners stack newer controls on top, so the compass and pin sit above the zoom buttons.
    this.basemap = new MapBasemap({
      map: this.map,
      creditEl: credit,
      onStyleReady: () => {
        this.ensureLayers();
        this.userLocation.reconcileLayers();
        this.syncHomeQuestSpots();
        this.highlight.reconcile();
        this.applyQuestRouteStyles();
        this.applyBrowseLayers();
        this.renderDraft();
        this.renderIssues();
        this.renderBoundary();
        this.renderWorkArea();
        if (this.workArea) this.readRoadsWhenSettled();
      },
      onBasemapChange: () => {
        this.basemap.reconcileLayerStack();
        this.userLocation.reconcileLayers();
        this.syncHomeQuestSpots();
        this.highlight.reconcile();
        if (this.workArea) this.readRoadsWhenSettled();
      },
      onToast: (message) => this.onStatus(message),
    });
    this.viewControls = new MapViewControls({
      onCompass: () => this.onCompassPress?.(),
      onHome: () => this.showQuestStart(),
      map: this.map,
    });
    this.viewControls.prepend(this.basemap.getToggleElement());
    this.map.addControl(this.viewControls, "bottom-right");
    void this.basemap.boot().then(() => this.paintViewControls());
    this.map.addControl(new maplibregl.ScaleControl({ maxWidth: 100 }), "bottom-left");

    this.map.on("load", () => {
      this.ensureLayers();
      this.ready = true;
      this.applyQuestRouteStyles();
      this.applyBrowseLayers();
      this.renderDraft();
      this.renderIssues();
      this.renderBoundary();
      this.renderWorkArea();
      if (this.workArea) this.readRoadsWhenSettled();
      this.refreshCanvasRect();
      this.refreshCursor();
    });
    this.map.on("idle", () => this.onMapIdle());
    this.map.on("click", (e) => this.onMapClick(e));
    this.map.on("sourcedata", (event) => this.onRoadSourceData(event));
    this.map.on("move", () => {
      this.invalidateScreenGrid();
      if (this.vertexDrag) this.vertexDrag.snapGrid = null;
      if (this.mode === "erase") this.paintEraseBrush();
      else if (this.mode === "roadsCircle") this.paintRoadsBrush();
    });
    this.map.on("resize", () => this.refreshCanvasRect());
    this.map.on("moveend", () => {
      this.northTarget = null;
      this.paintViewControls();
    });
    const userTurned = (event: { originalEvent?: unknown }) => {
      if (event.originalEvent) this.northMemory = null;
    };
    this.map.on("rotatestart", userTurned);
    this.map.on("pitchstart", userTurned);
    this.map.on("zoomstart", (event: { originalEvent?: unknown }) => {
      if (!event.originalEvent) return;
      this.camera.cancelReveal();
    });
    this.map.on("rotate", () => this.paintViewControls());
    this.paintViewControls();

    for (const layer of [NEARBY_LAYER, NEARBY_CLUSTER]) {
      this.map.on("mouseenter", layer, () => {
        this.overNearby = true;
        this.refreshCursor();
      });
      this.map.on("mouseleave", layer, () => {
        this.overNearby = false;
        this.refreshCursor();
      });
    }

    // Window capture runs before MapLibre's own pointer handlers, so a segment
    // or vertex drag can claim the gesture before the map starts panning.
    window.addEventListener("pointerdown", this.onPointerDown, { capture: true, passive: false });
    window.addEventListener("pointermove", this.onPointerMove, { capture: true, passive: true });
    window.addEventListener("pointerup", this.onPointerUp, { capture: true });
    window.addEventListener("pointercancel", this.onPointerUp, { capture: true });
    this.map.getCanvas().addEventListener("mousemove", this.onCanvasMouseMove, { passive: true });
    window.addEventListener("keydown", this.onCursorKey, true);
    window.addEventListener("keyup", this.onCursorKey, true);
    window.addEventListener("blur", this.onCursorBlur);
    window.addEventListener("scroll", this.refreshCanvasRect, true);
    window.addEventListener("wheel", this.onWheel, { capture: true, passive: false });
    this.surface.addEventListener("pointerleave", this.onSurfaceLeave);
    const resizeObserver = new ResizeObserver(() => this.refreshCanvasRect());
    resizeObserver.observe(this.map.getContainer());
    this.refreshCanvasRect();
    this.refreshCursor();
  }

  magnetEnabled(): boolean {
    return this.magnetOn;
  }

  setMagnet(on: boolean): void {
    this.magnetOn = on;
    try {
      writeSnapPref(localStorage, on);
    } catch {
      /* storage unavailable — the choice still applies this session */
    }
    if (!on) this.clearSnapMark();
    this.onStatus(t(on ? "snap.on" : "snap.off"));
  }

  crosshairEnabled(): boolean {
    return this.crosshairOn;
  }

  setCrosshair(on: boolean): void {
    this.crosshairOn = on;
    this.notify(false);
  }

  pointsVisible(): boolean {
    return this.showPoints;
  }

  setKeymap(keymap: Keymap, platform: Platform = detectPlatform()): void {
    this.keymap = keymap;
    this.inputPlatform = platform;
    this.refreshCursor();
  }

  /** Settings and rebinding own the pointer until they close. */
  setInputSuspended(on: boolean): void {
    this.inputSuspended = on;
  }

  setPointsVisible(on: boolean): void {
    this.showPoints = on;
    try {
      writeShowPoints(localStorage, on);
    } catch {
      /* storage unavailable — the choice still applies this session */
    }
    if (!on && this.selection.length > 0) {
      this.selection = [];
      this.clickOrder = null;
      this.orderAnchor = null;
    }
    this.renderDraft();
    this.notify(false);
  }

  getCoordinates(): LngLat[] {
    return this.points.map(clone);
  }

  pointCount(): number {
    return this.points.length;
  }

  /** Odd-degree vertices. A coordinate edit does not change this; the scan is a few tenths of a millisecond. */
  oddVertexCount(): number {
    const n = this.points.length;
    const deg = new Uint32Array(n);
    for (const [a, b] of this.segments) {
      if (a >= 0 && a < n) deg[a] += 1;
      if (b >= 0 && b < n) deg[b] += 1;
    }
    let odd = 0;
    for (let i = 0; i < n; i++) if ((deg[i] & 1) === 1) odd += 1;
    return odd;
  }

  routeIsFresh(): boolean {
    return this.routeCache != null && this.routeCache.tick === this.routeTick;
  }

  /** Last run, including one that a newer edit has not recomputed yet. */
  cachedRoute(): RouteResult | null {
    return this.routeCache?.result ?? null;
  }

  /** Connected parts of the drawing, lone points included. */
  partCount(): number {
    return this.currentParts().count;
  }

  /** Gap, overlap, and crossing counts from the last check. Null in view mode or before the first check. */
  issueCounts(): RouteIssueCounts | null {
    if (this.browseMode || !this.issues) return null;
    return {
      gaps: this.issueSpots.gap.length,
      overlaps: this.issueSpots.overlap.length,
      crossings: this.issueSpots.crossing.length,
    };
  }

  /** Zoom to issue `index` of `kind`, wrapping. False when there is none. */
  focusIssue(kind: RouteIssueKind, index: number): boolean {
    const targets = this.issueTargets(kind);
    if (targets.length === 0) return false;
    const [sw, ne] = targets[((index % targets.length) + targets.length) % targets.length];
    this.camera.focusSpan(sw, ne, haversineM(sw, ne) < 5);
    return true;
  }

  /** Select every point outside the largest part, so it can be joined or deleted. Returns the count. */
  selectSmallerParts(): number {
    if (this.blocking > 0 || this.grab || this.deleteMenuOpen || this.browseMode) return 0;
    const parts = this.currentParts();
    const stray = smallerPartPoints(parts);
    if (stray.length === 0) return 0;
    if (this.showPoints) {
      this.selection = stray;
      this.selectedLines = [];
    } else {
      const lines: number[] = [];
      for (let s = 0; s < this.segments.length; s++) {
        if (this.decorative[s]) continue;
        const p = parts.partOf[this.segments[s][0]];
        if (p < 0 || p !== parts.largest) lines.push(s);
      }
      if (lines.length === 0) return 0;
      this.selection = [];
      this.selectedLines = lines;
    }
    this.clickOrder = null;
    this.orderAnchor = this.selection[0] ?? null;
    this.renderDraft();
    this.notify(false);
    return this.selectionCount();
  }

  private currentParts(): ConnectedParts {
    if (!this.partsCache || this.partsCache.tick !== this.routeTick) {
      this.partsCache = {
        tick: this.routeTick,
        parts: routeConnectivityParts(this.points.length, this.segments, this.decorative),
      };
    }
    return this.partsCache.parts;
  }

  private issueTargets(kind: RouteIssueKind): Array<[LngLat, LngLat]> {
    if (kind === "part") return smallerPartBounds(this.points, this.currentParts());
    if (this.browseMode || !this.issues) return [];
    if (kind === "gap") return this.issueSpots.gap;
    const spots = kind === "overlap" ? this.issueSpots.overlap : this.issueSpots.crossing;
    return spots.map((p) => [p, p]);
  }

  /** Recheck the drawing once edits pause. Drags and grabs wait for the drop. */
  private scheduleIssues(): void {
    if (this.browseMode || this.issuesTick === this.routeTick) return;
    if (this.issuesTimer && this.issuesPendingTick === this.routeTick) return;
    if (this.issuesTimer) window.clearTimeout(this.issuesTimer);
    this.issuesPendingTick = this.routeTick;
    this.issuesTimer = window.setTimeout(() => {
      this.issuesTimer = 0;
      if (this.browseMode || this.vertexDrag?.moved || this.grab) return;
      this.refreshIssues();
      this.onChange(false);
    }, ISSUE_DELAY_MS);
  }

  private refreshIssues(): void {
    this.issuesTick = this.routeTick;
    const issues = findRouteIssues(this.points, this.segments, {}, this.decorative);
    this.issues = issues;
    this.issueSpots = {
      gap: issues.gaps.map((g) => {
        const p = this.points[g.point];
        const sw: LngLat = [Math.min(p[0], g.target[0]), Math.min(p[1], g.target[1])];
        const ne: LngLat = [Math.max(p[0], g.target[0]), Math.max(p[1], g.target[1])];
        return [sw, ne];
      }),
      overlap: overlapSpots(issues, this.segments),
      crossing: crossingSpots(issues),
    };
    this.renderIssues();
  }

  /** Markers go stale while a vertex moves, so they clear until the drop is checked. */
  private hideIssuesUntilChecked(): void {
    this.issuesTick = -1;
    if (!this.ready) return;
    const src = this.map.getSource(ISSUE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    src?.setData({ type: "FeatureCollection", features: [] });
  }

  private renderIssues(): void {
    if (!this.ready) return;
    this.ensureLayers();
    const src = this.map.getSource(ISSUE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    src.setData(
      this.issues
        ? routeIssueFeatures(this.points, this.segments, this.issues, this.decorative)
        : { type: "FeatureCollection", features: [] },
    );
  }

  getGraph(): PieceGraph {
    const graph: PieceGraph = {
      points: this.points.map(clone),
      segments: this.segments.map(([a, b]) => [a, b]),
      start: this.start,
    };
    if (this.workArea) graph.workArea = serializeWorkArea(this.workArea);
    if (this.regions.length > 0) graph.regions = cloneRegions(this.regions);
    const pointStyles = packPointStyles(this.pointStyles, this.points.length);
    const lineStyles = packLineStyles(this.lineStyles, this.segments.length);
    if (pointStyles) graph.pointStyles = pointStyles;
    if (lineStyles) graph.lineStyles = lineStyles;
    const decorative = packDecorative(this.decorative, this.segments.length);
    if (decorative) graph.decorative = decorative;
    if (this.pieceStyleLocked) graph.styleDefaults = parseStyleDefaults(this.nextStyleDefaults);
    return graph;
  }

  /** Settings defaults for the next point or line, unless this piece has its own. */
  setStyleDefaults(defaults: StyleDefaults): void {
    this.settingsStyleDefaults = parseStyleDefaults(defaults);
    if (!this.pieceStyleLocked) this.nextStyleDefaults = this.settingsStyleDefaults;
  }

  browsing(): boolean {
    return this.browseMode;
  }

  /**
   * View mode hides handles, work-area dimming, selection, and tool cursors.
   * Clicks do not select. Pan, zoom, and rotate still work.
   */
  setBrowseMode(on: boolean): void {
    if (this.browseMode === on) {
      this.applyBrowseLayers();
      return;
    }
    if (!on) this.browseViewBeforeEdit = this.captureBrowseView();
    this.browseMode = on;
    if (on) {
      if (this.grab) this.cancelGrab();
      this.leaveErase();
      this.cancelRoadsGestures();
      this.mode = "graph";
      this.forgetSelection();
      this.workPreview = null;
      this.canvasEditDrag = null;
      this.clearOverlay();
      this.restoreTouchPan();
    }
    this.applyBrowseLayers();
    this.renderWorkArea();
    this.renderDraft();
    this.renderBoundary();
    this.refreshCursor();
    this.paintViewControls();
    if (on) this.restoreBrowseViewAfterEdit();
    this.notify(false);
  }

  private captureBrowseView(): (SavedMapView & { framed: boolean }) | null {
    try {
      const c = this.map.getCenter();
      if (!finiteMapCenter(c)) return null;
      return {
        center: [c.lng, c.lat],
        zoom: this.map.getZoom(),
        bearing: this.map.getBearing(),
        pitch: this.map.getPitch(),
        framed: this.camera.isFramed(),
      };
    } catch {
      return null;
    }
  }

  /** Current home browse camera for sessionStorage when leaving the home map. */
  captureBrowseViewForPersist(): (SavedMapView & { framed: boolean }) | null {
    if (!this.browseMode) return null;
    return this.captureBrowseView();
  }

  /** Apply a tab-persisted browse view after cross-page navigation back to home. */
  applyPersistedBrowseView(view: SavedMapView & { framed: boolean }): void {
    this.skipNextQuestResizeRefit = true;
    this.camera.setFramed(view.framed);
    this.camera.restoreView(view, 0);
  }

  /** Return to the pre-edit browse camera, or frame the edited route — never default Seoul. */
  private restoreBrowseViewAfterEdit(): void {
    const saved = this.browseViewBeforeEdit;
    this.browseViewBeforeEdit = null;
    if (saved) {
      this.skipNextQuestResizeRefit = true;
      this.camera.setFramed(saved.framed);
      this.camera.restoreView(saved);
      this.onRestoreBrowseAfterEdit?.();
      return;
    }
    if (this.questFitLine()) {
      this.showQuestStart();
      this.onRestoreBrowseAfterEdit?.();
      return;
    }
    if (this.points.length > 0) {
      this.camera.fitPoints(this.points, MIN_EDIT_ZOOM + 1);
      this.onRestoreBrowseAfterEdit?.();
      return;
    }
    if (this.workArea) {
      this.camera.fitCanvas(canvasBounds(this.workArea), canvasBearing(this.workArea));
      this.onRestoreBrowseAfterEdit?.();
      return;
    }
    this.onRestoreBrowseAfterEdit?.();
  }

  styleDefaults(): StyleDefaults {
    return parseStyleDefaults(this.nextStyleDefaults);
  }

  resetPieceStyleDefaults(): void {
    this.pieceStyleLocked = false;
    this.nextStyleDefaults = parseStyleDefaults(this.settingsStyleDefaults);
  }

  /** Points and lines the Style panel can edit. */
  styleTargets(): StyleTargets {
    return collectStyleTargets(
      this.points.length,
      this.segments,
      this.selection,
      this.selectedLines,
      this.highlightMode(),
    );
  }

  styleForm(): StyleFormValues & StyleTargets & ReturnType<typeof decorativeFormValues> {
    const targets = this.styleTargets();
    return {
      ...styleFormValues(this.pointStyles, this.lineStyles, targets, this.nextStyleDefaults),
      ...targets,
      ...decorativeFormValues(this.decorative, this.segments.length, targets.lines),
    };
  }

  decorativeStrokeEnabled(): boolean {
    return this.decorativeStroke;
  }

  /** Toggle decorative (장식선) on the selection, or stroke mode for new lines. */
  toggleDecorative(): void {
    if (this.blocking > 0 || this.browseMode) return;
    const targets = this.styleTargets();
    if (targets.lines.length === 0) {
      this.decorativeStroke = !this.decorativeStroke;
      this.onStatus(t(this.decorativeStroke ? "decorativeStroke.on" : "decorativeStroke.off"));
      this.renderDraft();
      this.notify(false);
      return;
    }
    const next = toggleDecorativeOnLines(this.decorative, this.segments.length, targets.lines);
    if (!next.changed) return;
    this.commit(() => {
      this.decorative = next.decorative;
    });
  }

  /**
   * Paint the current selection. A null side is left alone, so a mixed
   * selection can update points, lines, or both. One undo step.
   */
  applyStyles(point: PointStyle | null, line: LineStyle | null): void {
    if (this.blocking > 0 || this.browseMode) return;
    const targets = this.styleTargets();
    const next = applyStylePatch(
      this.pointStyles,
      this.lineStyles,
      this.points.length,
      this.segments.length,
      targets,
      point ? normalizePointStyle(point) : null,
      line ? normalizeLineStyle(line) : null,
    );
    if (!next.changed) return;
    this.commit(() => {
      this.pointStyles = next.pointStyles;
      this.lineStyles = next.lineStyles;
    });
  }

  /**
   * Copy the first selected point style and the first selected line style.
   * Nothing selected leaves the clipboard as it is.
   */
  copySelectionStyle(): void {
    if (this.blocking > 0 || this.browseMode) return;
    const copied = copySelectionStyles(
      this.pointStyles,
      this.lineStyles,
      this.styleTargets(),
      this.nextStyleDefaults,
    );
    if (!copied) return;
    this.styleClipboard = copied;
  }

  /**
   * Paint the selection with the copied style of the same kind.
   * Points ignore a line style and lines ignore a point style.
   * An empty clipboard, or a selection with nothing of the copied kind, does nothing.
   * A paste also becomes this piece's default for later points and lines.
   * One undo step covers the whole paste.
   */
  pasteSelectionStyle(): void {
    if (this.blocking > 0 || this.browseMode || !this.styleClipboard) return;
    const clip = this.styleClipboard;
    const next = pasteSelectionStyles(
      this.pointStyles,
      this.lineStyles,
      this.points.length,
      this.segments.length,
      this.styleTargets(),
      clip,
    );
    const nextDefaults: StyleDefaults = {
      point: clip.point ? clonePointStyle(clip.point) : this.nextStyleDefaults.point,
      line: clip.line ? cloneLineStyle(clip.line) : this.nextStyleDefaults.line,
    };
    const defaultsChanged =
      !samePointStyle(nextDefaults.point, this.nextStyleDefaults.point) ||
      !sameLineStyle(nextDefaults.line, this.nextStyleDefaults.line) ||
      !this.pieceStyleLocked;
    if (!next.changed && !defaultsChanged) return;
    this.commit(() => {
      this.pointStyles = next.pointStyles;
      this.lineStyles = next.lineStyles;
      this.nextStyleDefaults = parseStyleDefaults(nextDefaults);
      this.pieceStyleLocked = true;
    });
  }

  /** Computed run. Empty until every segment is in one connected graph. */
  getSaveCoordinates(): LngLat[] {
    const route = this.getRoute();
    return route.ok ? route.coordinates.map(clone) : [];
  }

  getRoute(): RouteResult {
    if (this.routeCache && this.routeCache.tick === this.routeTick) return this.routeCache.result;
    const result = computeRoute({
      points: this.points,
      segments: this.segments,
      start: this.start,
      decorative: this.decorative,
    });
    this.routeCache = { tick: this.routeTick, result };
    return result;
  }

  isClosed(): boolean {
    if (this.browseMode) return false;
    const route = this.getRoute();
    return route.ok && route.closed;
  }

  hasOffRoad(): boolean {
    return this.offRoad;
  }

  canEditAtZoom(): boolean {
    return this.map.getZoom() >= MIN_EDIT_ZOOM;
  }

  canUndo(): boolean {
    return this.past.length > 0;
  }

  canRedo(): boolean {
    return this.future.length > 0;
  }

  /** The one selected point, when connect and Start should use it. */
  selectedIndex(): number | null {
    if (this.selectedLines.length > 0) return null;
    return this.selection.length === 1 ? (this.selection[0] ?? null) : null;
  }

  hasSelection(): boolean {
    return this.selection.length > 0 || this.selectedLines.length > 0;
  }

  /** Selected points, or the line count when the selection is lines only. */
  selectionCount(): number {
    return this.selection.length > 0 ? this.selection.length : this.selectedLines.length;
  }

  /**
   * Cycle every visible segment: all → non-decorative → clear.
   * A grab or the delete menu swallows the key.
   */
  selectAll(): void {
    if (this.blocking > 0 || this.grab || this.deleteMenuOpen) return;
    const lastPhase =
      this.selectAllCycleRevision === this.selectionRevision ? this.selectAllCyclePhase : 0;
    const result = cycleSelectAll(
      this.showPoints,
      this.points.length,
      this.segments,
      this.decorative,
      lastPhase,
    );
    const next = result.next;
    if (next.points.length === 0 && next.lines.length === 0) {
      if (!this.hasSelection()) return;
      this.forgetSelection();
    } else {
      this.selection = next.points;
      this.selectedLines = next.lines;
      this.clickOrder = null;
      this.orderAnchor = this.selection[0] ?? null;
    }
    this.selectAllCycleRevision = this.selectionRevision;
    this.selectAllCyclePhase = result.phase;
    this.onStatus(t(`toast.selectAll.${result.toast}`));
    this.renderDraft();
    this.notify(false);
  }

  /** A full line selection with points hidden paints in one pass. */
  private highlightMode() {
    return lineHighlightFor(this.showPoints, this.selection, this.selectedLines, this.segments.length);
  }

  /**
   * Connect the selected points. Two points get one segment. More than two
   * follow click order, or a nearest-neighbor chain when the selection came
   * from a lasso, box, circle, or select-all. One undo step. Already-present
   * segments are left as they are.
   */
  joinSelected(): "few" | "ok" {
    if (!this.canJoinOrMerge()) return "ok";
    const selected = this.pointSelection();
    if (selected.length < 2) return "few";
    const order = chainOrder(this.points, selected, this.clickOrder, this.orderAnchor);
    const next = joinSegments(this.segments, order);
    if (!next) return "ok";
    this.commit(() => {
      this.pendingJoinOrder = order;
      this.segments = next;
    });
    return "ok";
  }

  /**
   * Collapse the selected points into one at their centroid. Snap follows the
   * toolbar when a Work Area has roads. Lines move onto that point, self-loops
   * drop, and duplicates collapse. The result stays selected. One undo step.
   */
  mergeSelected(): "few" | "ok" {
    if (!this.canJoinOrMerge()) return "ok";
    const selected = this.pointSelection();
    if (selected.length < 2) return "few";
    const center = centroid(this.points, selected);
    if (!center) return "few";
    const at = this.magnetOn && this.workArea && this.roads.length > 0 ? this.maybeSnap(center) : center;
    const merged = mergePoints(this.points, this.segments, this.start, selected, at);
    if (!merged) return "few";
    this.commit(() => {
      this.pendingPointMap = merged.pointMap;
      this.points = merged.points;
      this.segments = merged.segments;
      this.start = merged.start;
      this.selection = [merged.index];
      this.selectedLines = [];
      this.clickOrder = [merged.index];
      this.orderAnchor = merged.index;
    });
    return "ok";
  }

  /** Clear the selection. Escape still does this, and also leaves Area, Erase, and Work Area. */
  deselectAll(): void {
    if (this.blocking > 0 || this.grab || this.deleteMenuOpen) return;
    if (!this.hasSelection()) return;
    this.forgetSelection();
    this.renderDraft();
    this.notify(false);
  }

  /** Box, Circle, Lasso, or Off. Off until a saved choice says otherwise. */
  selectTool(): SelectTool {
    return this.selectionTool;
  }

  roadsToolKind(): RoadsTool {
    return this.roadsTool;
  }

  /**
   * Box → Area → Circle → Box. Only while Fill Roads is open.
   * A grab or the delete menu swallows the key.
   */
  cycleRoadsTool(): RoadsTool | null {
    if (this.grab || this.deleteMenuOpen) return null;
    this.cancelRoadsGestures();
    this.roadsTool = nextRoadsTool(this.roadsTool);
    try {
      writeRoadsTool(localStorage, this.roadsTool);
    } catch {
      /* the new tool still applies this session */
    }
    this.applyRoadsToolMode();
    return this.roadsTool;
  }

  enterRoadsFill(): void {
    this.cancelRoadsGestures();
    this.applyRoadsToolMode();
  }

  leaveRoadsFill(): void {
    this.cancelRoadsGestures();
    this.clearBoundary();
    if (this.mode === "boundary" || this.mode === "roadsBox" || this.mode === "roadsCircle") {
      this.mode = "graph";
    }
    this.restoreTouchPan();
    this.refreshSelectCircle();
    this.refreshCursor();
    this.notify(false);
  }

  /**
   * Box → Circle → Lasso → Off → Box. A grab or the delete menu swallows the key,
   * and the toolbar uses the same guard.
   */
  cycleSelectTool(): SelectTool | null {
    if (this.grab || this.deleteMenuOpen) return null;
    this.selectionTool = nextSelectTool(this.selectionTool);
    try {
      writeSelectTool(localStorage, this.selectionTool);
    } catch {
      /* the new tool still applies this session */
    }
    this.refreshSelectCircle();
    return this.selectionTool;
  }

  isGrabbing(): boolean {
    return this.grab != null;
  }

  /** Points that follow the cursor, including both ends of a selected line. */
  grabCount(): number {
    return this.grab?.indices.length ?? 0;
  }

  /** Start a modal move. The anchor snaps to roads when Snap is on. */
  beginGrabMove(): void {
    if (this.grab || this.mode !== "graph" || !this.cursor) return;
    if (
      this.vertexDrag ||
      this.segmentDrag ||
      this.lassoDrag ||
      this.viewDrag ||
      this.placeClick ||
      this.roadsBoxDrag ||
      this.roadsCircleDrag
    ) {
      return;
    }
    const indices = grabTargets(this.points.length, this.selection, this.selectedLines, this.segments);
    const anchor = this.grabAnchorIndex(indices);
    if (anchor == null) return;
    this.grab = {
      indices,
      anchor,
      origins: this.points.map(clone),
      origin: this.unprojectLocal(this.cursor),
      before: this.snapshot(),
    };
    this.hideIssuesUntilChecked();
    this.refreshSelectCircle();
    this.refreshCursor();
    this.notify(false);
  }

  confirmGrab(): void {
    const grab = this.grab;
    if (!grab) return;
    const changed = grabChanged(grab.origins, this.points);
    this.grab = null;
    this.refreshCursor();
    if (!changed) {
      this.refreshSelectCircle();
      this.notify(false);
      return;
    }
    this.past = commitGrab(this.past, grab.before, true, HISTORY_LIMIT);
    this.future = [];
    this.routeTick += 1;
    this.renderDraft();
    this.refreshSelectCircle();
    this.notify(true);
  }

  cancelGrab(): void {
    const grab = this.grab;
    if (!grab) return;
    this.points = grab.origins.map(clone);
    this.grab = null;
    this.renderDraft();
    this.refreshSelectCircle();
    this.refreshCursor();
    this.notify(false);
  }

  /**
   * One sample per animation frame. The anchor follows the cursor and, when
   * Snap is on, sticks to a work-area road the same way a dragged point does.
   * Everyone else keeps that offset. Recolors only segments that touch a moved point.
   */
  private previewGrab(): void {
    const grab = this.grab;
    if (!grab || !this.cursor) return;
    const anchor = grab.origins[grab.anchor];
    if (!anchor) return;
    const delta = grabDelta(grab.origin, this.unprojectLocal(this.cursor));
    const free: LngLat = [anchor[0] + delta.lng, anchor[1] + delta.lat];
    const next = moveGrabTo(grab.origins, grab.indices, grab.anchor, this.maybeSnap(free), this.workArea);
    if (!grabChanged(this.points, next)) return;
    this.points = next;
    this.renderDraft();
  }

  /** Screen-space nearest grabbed point. Ties keep the earlier index. */
  private grabAnchorIndex(indices: readonly number[]): number | null {
    if (!this.cursor || indices.length === 0) return null;
    const positions: Array<[number, number] | undefined> = [];
    for (const index of indices) {
      const point = this.points[index];
      if (!point) continue;
      const screen = this.map.project(point);
      positions[index] = [screen.x, screen.y];
    }
    return grabAnchor(positions, indices, [this.cursor.x, this.cursor.y]);
  }

  interactionMode(): InteractionMode {
    return this.mode;
  }

  boundaryIsClosed(): boolean {
    return this.boundaryClosed;
  }

  canCloseBoundary(): boolean {
    return this.boundary.length >= 3 && !this.boundaryClosed;
  }

  hasBoundary(): boolean {
    return this.boundary.length > 0;
  }

  hasFill(): boolean {
    return this.fill != null;
  }

  spacingMeters(): number {
    return this.spacing;
  }

  eraseBrushRadius(): number {
    return this.eraseRadius;
  }

  setEraseBrushRadius(radius: number): void {
    const next = clampEraseRadius(radius);
    if (next === this.eraseRadius) return;
    this.eraseRadius = next;
    try {
      writeEraseRadius(localStorage, next);
    } catch {
      /* storage unavailable */
    }
    if (this.mode === "erase") this.paintEraseBrush();
  }

  setBoundaryMode(on: boolean): void {
    if (this.grab) this.cancelGrab();
    this.leaveErase();
    this.cancelRoadsGestures();
    this.mode = on ? "boundary" : "graph";
    this.restoreTouchPan();
    this.refreshSelectCircle();
    this.refreshCursor();
    this.notify(false);
  }

  private applyRoadsToolMode(): void {
    if (this.grab) this.cancelGrab();
    this.leaveErase();
    if (this.roadsTool === "area") {
      this.mode = "boundary";
      this.restoreTouchPan();
    } else if (this.roadsTool === "box") {
      this.mode = "roadsBox";
      this.map.dragPan.disable();
      this.clearOverlay();
    } else {
      this.mode = "roadsCircle";
      this.map.dragPan.disable();
      this.paintRoadsBrush();
    }
    this.refreshSelectCircle();
    this.refreshCursor();
    this.notify(false);
  }

  private cancelRoadsGestures(): void {
    if (!this.roadsBoxDrag && !this.roadsCircleDrag) return;
    this.roadsBoxDrag = null;
    this.roadsCircleDrag = null;
    this.clearOverlay();
    this.releaseDragPan();
  }

  setEraseMode(on: boolean): void {
    if (this.grab) this.cancelGrab();
    if (!on) this.leaveErase();
    this.mode = on ? "erase" : "graph";
    if (on) {
      this.map.dragPan.disable();
      this.paintEraseBrush();
    } else {
      this.restoreTouchPan();
      this.refreshSelectCircle();
    }
    this.refreshCursor();
    this.notify(false);
  }

  setWorkMode(on: boolean): void {
    if (this.grab) this.cancelGrab();
    this.leaveErase();
    this.mode = on ? "work" : "graph";
    if (on) this.map.dragPan.disable();
    else this.restoreTouchPan();
    this.refreshSelectCircle();
    this.refreshCursor();
    this.notify(false);
  }

  setFillPaletteOpen(open: boolean): void {
    this.fillPaletteOpen = open;
  }

  /** Remember the swatch and, when Fill Region is already on, retint the bucket. */
  setRegionColor(color: string): void {
    if (color === NO_FILL) {
      this.regionColor = NO_FILL;
      if (this.mode === "fill") this.refreshCursor();
      return;
    }
    const hex = normalizeColor(color);
    if (!hex) return;
    this.regionColor = hex;
    if (this.mode === "fill") this.refreshCursor();
  }

  regionColorValue(): string {
    return this.regionColor;
  }

  setFillMode(on: boolean): void {
    if (this.grab) this.cancelGrab();
    this.leaveErase();
    this.mode = on ? "fill" : "graph";
    if (on) this.map.dragPan.disable();
    else this.restoreTouchPan();
    this.refreshSelectCircle();
    this.refreshCursor();
    this.notify(false);
  }

  /**
   * Paint the closed face under the cursor. An existing fill of that face
   * changes color. One undo step. Returns "none" when the click is not inside
   * a bounded face.
   */
  fillAtCursor(): "none" | "ok" {
    if (this.blocking > 0 || !this.cursor) return "none";
    if (this.regionColor === NO_FILL) return this.clearRegionAtCursor();
    const at = this.unprojectLocal(this.cursor);
    const faces = this.facesForHit();
    const face = faceAt(faces, this.points, at[0], at[1]);
    if (!face) return "none";
    const color = this.regionColor;
    const existing = this.regions.find((region) => region.cycle.join(",") === face.key);
    if (existing?.color === color) return "ok";
    this.commit(() => {
      const rest = this.regions.filter((region) => region.cycle.join(",") !== face.key);
      this.regions = [{ cycle: face.cycle.slice(), color, seed: [at[0], at[1]] }, ...rest];
    });
    return "ok";
  }

  /**
   * Paint every closed face, or clear every fill when the color is "none".
   * Faces stay unselectable. One undo step, and a no-op when nothing would change.
   */
  fillEveryFace(color: string): void {
    if (this.blocking > 0) return;
    if (color === NO_FILL) {
      this.clearEveryFace();
      return;
    }
    const hex = normalizeColor(color);
    if (!hex) return;
    const faces = this.facesForHit();
    if (faces.length === 0 || facesShareFill(this.regions, faces, hex)) return;
    const next = paintAllFaces(this.points, faces, hex);
    this.commit(() => {
      this.regions = next;
    });
  }

  private clearEveryFace(): void {
    if (this.regions.length === 0) return;
    this.commit(() => {
      this.regions = [];
    });
  }

  /** Remove the fill under the cursor. One undo step. */
  clearRegionAtCursor(): "none" | "ok" {
    if (this.blocking > 0 || !this.cursor) return "none";
    const at = this.unprojectLocal(this.cursor);
    const faces = this.facesForHit();
    const face = faceAt(faces, this.points, at[0], at[1]);
    if (!face) return "none";
    const covers = (region: RegionFill): boolean => {
      if (region.cycle.join(",") === face.key) return true;
      return faceAt([face], this.points, region.seed[0], region.seed[1]) != null;
    };
    if (!this.regions.some(covers)) return "ok";
    this.commit(() => {
      this.regions = this.regions.filter((region) => !covers(region));
    });
    return "ok";
  }

  hasWorkArea(): boolean {
    return this.workArea != null;
  }

  clearWorkArea(): void {
    if (!this.workArea && !this.workPreview && !this.canvasEditDrag) return;
    const before = this.snapshot();
    this.workArea = null;
    this.workPreview = null;
    this.canvasEditDrag = null;
    this.storeRoads([]);
    this.roadSourceIds = new Set();
    this.extractWatch = idleExtractWatch();
    window.clearTimeout(this.extractTimer);
    this.renderWorkArea();
    this.renderDraft();
    this.pushHistory(before);
    this.notify(true);
  }

  closeBoundary(): void {
    if (!this.canCloseBoundary()) return;
    this.boundaryClosed = true;
    this.renderBoundary();
    this.notify(false);
  }

  clearBoundary(): void {
    this.cancelRoadsGestures();
    if (this.boundary.length === 0 && !this.boundaryClosed) return;
    this.boundary = [];
    this.boundaryClosed = false;
    this.renderBoundary();
    this.notify(false);
  }

  /** Live resample of the latest fill. One undo step is recorded on release. */
  previewSpacing(meters: number): void {
    if (this.blocking > 0) return;
    const spacing = clampSpacing(meters);
    if (!this.fill) {
      this.spacing = spacing;
      this.notify(false);
      return;
    }
    if (!this.spacingGesture) this.spacingGesture = this.snapshot();
    this.applySnapshot(this.spacingGesture);
    this.writeSpacing(spacing);
    this.notify(false);
  }

  private cancelSpacingGesture(): void {
    if (!this.spacingGesture) return;
    this.applySnapshot(this.spacingGesture);
    this.spacingGesture = null;
  }

  commitSpacing(): void {
    if (this.blocking > 0 || !this.spacingGesture) return;
    const before = this.spacingGesture;
    this.spacingGesture = null;
    if (sameSnapshot(before, this.snapshot())) {
      this.notify(false);
      return;
    }
    this.past.push(before);
    if (this.past.length > HISTORY_LIMIT) this.past.shift();
    this.future = [];
    this.notify(true);
  }

  fillRoads(): void {
    if (this.blocking > 0) return;
    if (!this.boundaryClosed || this.boundary.length < 3) {
      this.onStatus(t("fill.closeFirst"));
      return;
    }
    if (polygonAreaM2(this.boundary) > MAX_FILL_AREA_M2) {
      this.onStatus(t("fill.tooLarge"));
      return;
    }
    this.fillClippedRoads(clipLinesToPolygon(this.roadLines(), this.boundary));
  }

  private fillRoadsInRing(ring: readonly LngLat[]): void {
    if (this.blocking > 0) return;
    if (ring.length < 3) return;
    if (polygonAreaM2(ring) > MAX_FILL_AREA_M2) {
      this.onStatus(t("fill.tooLarge"));
      return;
    }
    this.fillClippedRoads(clipLinesToPolygon(this.roadLines(), ring));
  }

  private fillRoadsAlongStroke(samples: readonly LngLat[], radiusM: number): void {
    if (this.blocking > 0) return;
    if (samples.length === 0) return;
    this.fillClippedRoads(clipLinesToStroke(this.roadLines(), samples, radiusM));
  }

  private roadLines(): LngLat[][] {
    return this.roads.map(([a, b]) => [clone(a), clone(b)]);
  }

  private fillClippedRoads(clipped: LngLat[][]): void {
    if (!this.workArea) {
      this.onStatus(t("workArea.need"));
      return;
    }
    if (this.roads.length === 0) {
      this.onStatus(this.extractWatch.awaitingTiles ? t("fill.zoom") : t("fill.empty"));
      return;
    }
    const vertices = clipped.reduce((sum, line) => sum + line.length, 0);
    if (vertices > MAX_FILL_INPUT_VERTICES) {
      this.onStatus(t("fill.tooMany"));
      return;
    }
    if (clipped.length === 0) {
      this.onStatus(t("fill.empty"));
      return;
    }
    void this.withWaitCursor(fillIsHeavy(clipped.length, vertices), () => {
      const welded = weldRoadLines(clipped, JUNCTION_TOLERANCE_M);
      const resampled = resampleRoadGraph(welded, this.spacing);
      if (resampled.graph.points.length === 0) {
        this.onStatus(t("fill.empty"));
        return;
      }
      if (resampled.graph.points.length > MAX_FILL_POINTS) {
        this.onStatus(t("fill.tooMany"));
        return;
      }
      this.commit(() => {
        const basePoints = this.points.map(clone);
        const merged = mergeGraphs(this.getGraph(), resampled.graph, COINCIDE_TOLERANCE_M);
        this.points = merged.points;
        this.segments = merged.segments;
        this.start = merged.start;
        this.forgetSelection();
        this.fill = {
          basePoints,
          welded,
          resampled: resampled.graph,
          covers: resampled.covers.map((cover) => cover.slice()),
        };
      });
      this.onStatus(t("fill.done"));
    });
  }

  placeAtCenter(): void {
    if (this.blocking > 0) return;
    const center = this.map.getCenter();
    if (this.mode === "erase" || this.mode === "roadsBox" || this.mode === "roadsCircle") return;
    if (this.mode === "boundary") {
      if (this.boundaryClosed) return;
      this.boundary.push([center.lng, center.lat]);
      this.renderBoundary();
      this.notify(false);
      return;
    }
    this.place([center.lng, center.lat]);
  }

  /** Place Point binding: the pointer's last position, with the same rules as a click. */
  placeAtCursor(): void {
    if (this.blocking > 0 || !this.cursor) return;
    this.editAt(this.cursor.x, this.cursor.y);
  }

  undo(): void {
    if (this.blocking > 0 || this.grab) return;
    this.cancelSpacingGesture();
    const prev = this.past.pop();
    if (!prev) return;
    this.future.push(this.snapshot());
    if (this.future.length > HISTORY_LIMIT) this.future.shift();
    this.applySnapshot(prev);
    this.notify(true);
  }

  redo(): void {
    if (this.blocking > 0 || this.grab) return;
    this.cancelSpacingGesture();
    const next = this.future.pop();
    if (!next) return;
    this.past.push(this.snapshot());
    if (this.past.length > HISTORY_LIMIT) this.past.shift();
    this.applySnapshot(next);
    this.notify(true);
  }

  clear(): void {
    if (this.blocking > 0) return;
    const drawn = this.points.length > 0 || this.segments.length > 0 || this.fill != null || this.regions.length > 0;
    if (!drawn && !this.workArea) return;
    if (drawn) {
      this.commit(() => {
        this.points = [];
        this.segments = [];
        this.start = null;
        this.forgetSelection();
        this.fill = null;
        this.regions = [];
      });
    }
    if (this.workArea) this.clearWorkArea();
  }

  /** Replace the graph from a saved piece or a restored draft. Not an undo step. */
  loadGraph(graph: PieceGraph): void {
    if (this.blocking > 0) return;
    if (this.grab) this.cancelGrab();
    const clean = sanitizeGraph(graph);
    this.points = clean.points;
    this.segments = clean.segments;
    this.pointStyles = clonePointStyles(clean.pointStyles ?? [], clean.points.length);
    this.lineStyles = cloneLineStyles(clean.lineStyles ?? [], clean.segments.length);
    this.decorative = cloneDecorative(clean.decorative ?? [], clean.segments.length);
    this.start = clean.start;
    this.forgetSelection();
    this.fill = null;
    this.regions = cloneRegions(clean.regions ?? []);
    this.regionSig = 0;
    this.regionKnown = false;
    this.regionFaces = [];
    this.regionLayer.invalidate();
    this.past = [];
    this.future = [];
    this.workArea = clean.workArea ?? null;
    this.workPreview = null;
    if (clean.styleDefaults) {
      this.nextStyleDefaults = parseStyleDefaults(clean.styleDefaults);
      this.pieceStyleLocked = true;
    } else {
      this.nextStyleDefaults = parseStyleDefaults(this.settingsStyleDefaults);
      this.pieceStyleLocked = false;
    }
    this.storeRoads([]);
    this.extractWatch = this.workArea ? watchForNewArea(this.workArea) : idleExtractWatch();
    window.clearTimeout(this.extractTimer);
    this.routeTick += 1;
    this.renderWorkArea();
    this.renderDraft();
    // Home map selection frames via setQuestView / showQuestStart while a quest is selected.
    if (!this.questFitLine()) {
      if (this.workArea) this.fitWorkArea();
      else if (this.points.length > 0) this.camera.fitPoints(this.points, MIN_EDIT_ZOOM + 1);
    }
    this.notify(false);
    if (this.workArea) this.readRoadsWhenSettled();
  }

  /** GPX import becomes a chain of connected points. Undo returns to the previous graph. */
  importLine(coords: LngLat[]): void {
    if (this.blocking > 0) return;
    const graph = graphFromLine(simplifyToMax(coords, 8, 2000), false);
    this.commit(() => {
      this.points = graph.points;
      this.segments = graph.segments;
      this.start = graph.start;
      this.forgetSelection();
      this.fill = null;
    });
    this.camera.fitPoints(this.points, MIN_EDIT_ZOOM + 1);
  }

  deleteSelected(): void {
    if (this.blocking > 0) return;
    if (this.selection.length > 0) {
      this.deletePoints(this.selection, "dissolve");
      return;
    }
    this.deleteLines(this.selectedLines);
  }

  /** Which delete-menu rows would change the graph. All false means nothing is selected. */
  deleteMenuState(): { points: boolean; lines: boolean; pointsAndLines: boolean } {
    return deleteMenuEnabled(
      this.points.length,
      this.segments,
      this.selection,
      this.selectedLines,
      this.highlightMode(),
    );
  }

  /**
   * One undo step. Delete Points dissolves degree-2 points and drops the rest.
   * Delete Points and Lines removes the points and every line that touched them.
   * Delete Lines Only keeps the points.
   */
  confirmDeleteMenu(choice: DeleteMenuChoice): void {
    if (this.blocking > 0) return;
    if (choice === "lines") {
      this.deleteLines(linesForDelete(this.segments, this.selection, this.selectedLines, this.highlightMode()));
      return;
    }
    const points = pointsForDelete(this.points.length, this.selection, this.selectedLines, this.segments);
    this.deletePoints(points, choice === "pointsAndLines" ? "drop" : "dissolve");
  }

  pointerClient(): { x: number; y: number } {
    if (this.pointerAt) return { x: this.pointerAt.x, y: this.pointerAt.y };
    return {
      x: this.canvasRect.left + this.canvasRect.width / 2,
      y: this.canvasRect.top + this.canvasRect.height / 2,
    };
  }

  setDeleteMenuOpen(open: boolean): void {
    this.deleteMenuOpen = open;
  }

  setDeleteMenuDismiss(handler: () => void): void {
    this.onDeleteMenuDismiss = handler;
  }

  deselect(): void {
    if (this.blocking > 0) return;
    if (this.grab) this.cancelGrab();
    if (!this.hasSelection()) return;
    this.forgetSelection();
    this.renderDraft();
    this.notify(false);
  }

  setStartToSelected(): void {
    if (this.blocking > 0) return;
    const selected = this.selectedIndex();
    if (selected == null || this.start === selected) return;
    this.commit(() => {
      this.start = selected;
    });
  }

  viewportBBox(): {
    minLat: number;
    minLng: number;
    maxLat: number;
    maxLng: number;
  } {
    const b = this.map.getBounds();
    return {
      minLat: b.getSouth(),
      minLng: b.getWest(),
      maxLat: b.getNorth(),
      maxLng: b.getEast(),
    };
  }

  /** Clustered pins for the nearby list (at each quest's route bbox center). */
  setNearby(pins: readonly NearbyPin[]): void {
    if (!this.ready) return;
    this.ensureLayers();
    setNearbyPins(this.map, pins);
  }

  /** Preview route lines of nearby quests, under the open route. */
  setRouteLines(paths: readonly CachedRouteLine[]): void {
    if (!this.ready) return;
    this.ensureLayers();
    setCachedRouteLines(this.map, paths);
  }

  /** Open a nearby quest or expand its cluster at a canvas point. True when something was hit. */
  private pickNearbyAt(point: maplibregl.PointLike): boolean {
    const clusterHit = this.map.queryRenderedFeatures(point, { layers: [NEARBY_CLUSTER] });
    if (clusterHit[0]) {
      const clusterId = clusterHit[0].properties?.cluster_id;
      const src = this.map.getSource(NEARBY_SOURCE) as maplibregl.GeoJSONSource;
      if (typeof clusterId === "number") {
        void src.getClusterExpansionZoom(clusterId).then((zoom) => {
          const geom = clusterHit[0].geometry;
          if (geom.type !== "Point") return;
          this.camera.expandCluster(geom.coordinates as LngLat, zoom);
        });
      }
      return true;
    }

    const hit = this.map.queryRenderedFeatures(point, { layers: [NEARBY_LAYER] });
    const id = hit[0]?.properties?.id;
    if (typeof id === "string" && id.length > 0) {
      if (this.points.length > 0 && !this.browseMode) {
        this.onStatus(t("nearby.busy"));
        return true;
      }
      this.onNearbyClick(id);
      return true;
    }
    return false;
  }

  /** True when the click hits the selected quest's stroke on the home map (not fill regions). */
  private pickHomeQuestRouteAt(point: maplibregl.PointLike): boolean {
    const ids = HOME_QUEST_ROUTE_IDS;
    const layers = [ids.solid, ids.dashed, ids.dotted].filter((id) => this.map.getLayer(id));
    if (!layers.length) return false;
    return this.map.queryRenderedFeatures(point, { layers }).length > 0;
  }

  private onMapClick(e: maplibregl.MapMouseEvent): void {
    if (this.deleteMenuOpen || this.grab) return;
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }
    if (!this.ready) return;

    if (this.pickNearbyAt(e.point)) return;

    if (this.browseMode) {
      if (!this.pickHomeQuestRouteAt(e.point)) this.onBrowseMapBackgroundClick?.();
      return;
    }

    if (this.mode === "boundary") {
      this.onBoundaryClick(e);
      return;
    }
    if (this.mode === "erase" || this.mode === "work" || this.mode === "roadsBox" || this.mode === "roadsCircle")
      return;

    // Mouse and pen select on pointerup. A tap is the only way to add on a phone.
    if (pointerKind(e.originalEvent) !== "touch") return;

    if (this.hitVertex(e.point.x, e.point.y) != null) return;
    const segment = this.hitSegment(e.point.x, e.point.y, this.segmentHitPx(pointerKind(e.originalEvent)));
    if (segment != null) {
      this.insertOnSegment(segment, [e.lngLat.lng, e.lngLat.lat]);
      return;
    }

    this.place([e.lngLat.lng, e.lngLat.lat]);
  }

  /**
   * Drop a vertex on the segment and replace that segment with the two halves.
   * A selected point is linked to the new vertex so it can join the middle.
   */
  private insertOnSegment(segmentIndex: number, at: LngLat): void {
    if (!this.canEditAtZoom()) {
      this.notify(false);
      return;
    }
    if (this.outsideWorkArea(at)) return;
    const from = this.selectedIndex();
    if (splitSegment(this.points, this.segments, segmentIndex, at, from) == null) {
      const end = this.endpointNear(segmentIndex, at);
      if (end != null) this.activateVertex(end);
      return;
    }
    this.commit(() => {
      this.writeSplit(segmentIndex, at, from);
    });
  }

  private writeSplit(segmentIndex: number, at: LngLat, connectFrom: number | null): number | null {
    const split = splitSegment(this.points, this.segments, segmentIndex, at, connectFrom);
    if (!split) return null;
    this.points = split.points;
    this.segments = split.segments;
    this.selectOnly(split.index);
    return split.index;
  }

  private endpointNear(segmentIndex: number, at: LngLat): number | null {
    const seg = this.segments[segmentIndex];
    if (!seg) return null;
    const [a, b] = seg;
    const pa = this.points[a];
    const pb = this.points[b];
    if (!pa || !pb) return null;
    return haversineM(at, pa) <= haversineM(at, pb) ? a : b;
  }

  /** Click-to-add at a canvas pixel: connect, split a line, or place. */
  private editAt(x: number, y: number): void {
    if (this.mode === "erase" || this.mode === "roadsBox" || this.mode === "roadsCircle") return;
    const ll = this.unprojectLocal({ x, y });
    if (this.mode === "boundary") {
      if (this.boundaryClosed) return;
      const hit = this.hitBoundaryVertex(x, y, this.segmentHitPx("mouse"));
      if (hit === 0 && this.boundary.length >= 3) {
        this.closeBoundary();
        return;
      }
      if (hit != null) return;
      this.boundary.push(ll);
      this.renderBoundary();
      this.notify(false);
      return;
    }
    const vertex = this.hitVertex(x, y);
    if (vertex != null) {
      this.activateVertex(vertex);
      return;
    }
    const segment = this.hitSegment(x, y, this.segmentHitPx("mouse"));
    if (segment != null) {
      this.insertOnSegment(segment, ll);
      return;
    }
    this.place(ll);
  }

  private place(ll: LngLat): void {
    if (!this.canEditAtZoom()) {
      this.notify(false);
      return;
    }
    if (this.outsideWorkArea(ll)) return;
    const point = this.magnetOn ? this.maybeSnap(ll) : ll;
    const existing = this.pointNear(point);
    if (existing != null) {
      this.activateVertex(existing);
      return;
    }
    if (point !== ll) {
      const screen = this.map.project(point);
      const target = this.vertexSnapTarget({ x: screen.x, y: screen.y }, null);
      if (target?.kind === "vertex") {
        this.activateVertex(target.index);
        return;
      }
      if (target?.kind === "segment") {
        this.insertOnSegment(target.segment, this.unprojectLocal(target));
        return;
      }
    }
    const from = this.selectedIndex();
    this.commit(() => {
      this.points.push(point);
      const index = this.points.length - 1;
      if (from != null) this.link(from, index);
      this.selectOnly(index);
    });
  }

  private activateVertex(index: number): void {
    const current = this.selectedIndex();
    if (current != null && current !== index) {
      if (!this.canEditAtZoom()) {
        this.notify(false);
        return;
      }
      if (this.hasLink(current, index)) {
        this.selectOnly(index);
        this.renderDraft();
        this.notify(false);
        return;
      }
      this.commit(() => {
        this.link(current, index);
        this.selectOnly(index);
      });
      return;
    }
    if (current === index) this.forgetSelection();
    else this.selectOnly(index);
    this.renderDraft();
    this.notify(false);
  }

  private onBoundaryClick(e: maplibregl.MapMouseEvent): void {
    if (this.boundaryClosed) return;
    const radius = this.segmentHitPx(pointerKind(e.originalEvent));
    const hit = this.hitBoundaryVertex(e.point.x, e.point.y, radius);
    if (hit === 0 && this.boundary.length >= 3) {
      this.closeBoundary();
      return;
    }
    if (hit != null) return;
    this.boundary.push([e.lngLat.lng, e.lngLat.lat]);
    this.renderBoundary();
    this.notify(false);
  }

  private deleteAt(index: number): void {
    this.deletePoints([index], "dissolve");
  }

  /**
   * One undo step. Dissolve removes a degree-2 point and joins its neighbors.
   * Drop removes the points and every line that touched them.
   */
  private deletePoints(indices: readonly number[], mode: "dissolve" | "drop"): void {
    const drop = indices.filter((index) => index >= 0 && index < this.points.length);
    if (drop.length === 0) return;
    void this.withWaitCursor(deleteIsHeavy(this.segments.length, drop.length, 0), () => this.commit(() => {
      const doomed = new Set(drop);
      for (const [a, b] of this.segments) {
        if (!doomed.has(a) && !doomed.has(b)) continue;
        const pa = this.points[a];
        const pb = this.points[b];
        if (pa && pb) this.exciseFillGeometry(pa, pb);
      }
      const graph = {
        points: this.points,
        segments: this.segments,
        start: this.start,
        selection: this.selection,
      };
      const dissolved = mode === "dissolve" ? dissolvePoints(graph, drop) : null;
      const next = dissolved ?? deleteSelection(graph, drop);
      this.pendingPointMap = next.pointMap;
      if (dissolved) this.pendingCollapse = dissolved.collapsedFrom;
      this.points = next.points;
      this.segments = next.segments;
      this.start = next.start;
      this.selection = next.selection;
      this.selectedLines = [];
      this.noteBulk(this.selection[0] ?? null);
    }));
  }

  /** One undo step drops every listed line. The points stay, so only the line was deleted. */
  private deleteLines(indices: readonly number[]): void {
    const drop = [...new Set(indices.filter((index) => index >= 0 && index < this.segments.length))];
    if (drop.length === 0) return;
    void this.withWaitCursor(deleteIsHeavy(this.segments.length, 0, drop.length), () => this.commit(() => {
      for (const index of drop) {
        const segment = this.segments[index];
        if (!segment) continue;
        const a = this.points[segment[0]];
        const b = this.points[segment[1]];
        if (a && b) this.exciseFillGeometry(a, b);
      }
      const beforePoints = this.points.map((point) => [point[0], point[1]] as LngLat);
      const erased = eraseSegments(this.points, this.segments, drop, this.start, null, () => true);
      this.points = erased.points;
      this.segments = erased.segments;
      this.start = erased.start;
      this.selection = remapSelection(beforePoints, erased.points, this.selection);
      this.selectedLines = [];
      this.noteBulk(this.selection[0] ?? null);
    }));
  }

  private pointNear(ll: LngLat): number | null {
    const p = this.map.project(ll);
    return this.hitVertex(p.x, p.y);
  }

  private hasLink(a: number, b: number): boolean {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return this.segments.some(([x, y]) => x === lo && y === hi);
  }

  private link(a: number, b: number): void {
    if (a === b || this.hasLink(a, b)) return;
    this.segments.push([Math.min(a, b), Math.max(a, b)]);
    const index = this.segments.length - 1;
    if (this.decorativeStroke) {
      if (this.decorative.length < this.segments.length) {
        this.decorative = cloneDecorative(this.decorative, this.segments.length);
      }
      this.decorative[index] = true;
    } else if (this.decorative.length !== this.segments.length) {
      this.decorative = cloneDecorative(this.decorative, this.segments.length);
    }
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (this.blocking > 0) {
      e.preventDefault();
      return;
    }
    if (this.deleteMenuOpen && e.target instanceof Node && this.surface.contains(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      this.armIgnoreClick();
      this.onDeleteMenuDismiss?.();
      return;
    }
    if (this.inputSuspended || !this.ready) return;
    if (this.browseMode) {
      if (!(e.target instanceof Node) || !this.surface.contains(e.target)) return;
      this.pointerAt = { x: e.clientX, y: e.clientY };
      this.pointerOverMap = true;
      this.cursor = this.clientToLocal(e.clientX, e.clientY);
      this.syncMods(e);
      const fine = e.pointerType === "mouse" || e.pointerType === "pen";
      const rotate = this.viewAction(e) === "rotateView";
      if (fine && (rotate || e.button === 0 || this.viewAction(e) === "panView")) {
        this.camera.cancelReveal();
        this.takePointer(e);
        this.map.dragPan.disable();
        this.viewDrag = {
          pointerId: e.pointerId,
          action: rotate ? "rotateView" : "panView",
          x: e.clientX,
          y: e.clientY,
          lastX: e.clientX,
          lastY: e.clientY,
          moved: false,
          nudgeOk: false,
          nudged: false,
        };
        this.refreshCursor();
      }
      return;
    }
    if (!(e.target instanceof Node) || !this.surface.contains(e.target)) return;
    this.pointerAt = { x: e.clientX, y: e.clientY };
    this.pointerOverMap = true;
    if (this.grab) {
      e.preventDefault();
      e.stopPropagation();
      this.armIgnoreClick();
      if (this.pointerHits("confirmGrab", "click", e)) this.confirmGrab();
      else if (this.pointerHits("cancelGrab", "click", e)) this.cancelGrab();
      return;
    }
    this.cursor = this.clientToLocal(e.clientX, e.clientY);
    this.syncMods(e);
    if (e.altKey) e.preventDefault();
    const fine = e.pointerType === "mouse" || e.pointerType === "pen";
    if (fine && blocksEditing(this.keymap, this.pointerState(e, "drag"), this.inputPlatform)) {
      const action = this.viewAction(e);
      if (action) {
        this.camera.cancelReveal();
        this.takePointer(e);
        this.map.dragPan.disable();
        this.viewDrag = {
          pointerId: e.pointerId,
          action,
          x: e.clientX,
          y: e.clientY,
          lastX: e.clientX,
          lastY: e.clientY,
          moved: false,
          nudgeOk: false,
          nudged: false,
        };
        this.refreshCursor();
        return;
      }
    }
    if (this.fillPaletteOpen) {
      e.preventDefault();
      this.armIgnoreClick();
      return;
    }
    if (this.mode === "fill") {
      e.preventDefault();
      this.armIgnoreClick();
      if (this.pointerHits("exitFill", "click", e)) {
        this.setFillMode(false);
        return;
      }
      if (this.pointerHits("clearRegion", "click", e)) {
        if (this.clearRegionAtCursor() === "none") this.onStatus(t("hint.fillRegionNone"));
        return;
      }
      if (e.button === 0 && this.fillAtCursor() === "none") this.onStatus(t("hint.fillRegionNone"));
      return;
    }
    if (matchPointer(this.keymap, this.pointerState(e, "click"), this.inputPlatform) === "placeAtCenter") {
      this.placeClick = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
      e.preventDefault();
      return;
    }
    if (this.mode === "roadsBox") {
      if (!e.isPrimary) return;
      if (!this.wantsRoadsBox(e)) return;
      const at = this.clientToLocal(e.clientX, e.clientY);
      this.takePointer(e);
      this.map.dragPan.disable();
      this.roadsBoxDrag = {
        pointerId: e.pointerId,
        origin: at,
        x: e.clientX,
        y: e.clientY,
        moved: false,
      };
      return;
    }
    if (this.mode === "roadsCircle") {
      if (!e.isPrimary) return;
      if (!this.wantsRoadsCircle(e)) return;
      const at = this.clientToLocal(e.clientX, e.clientY);
      this.takePointer(e);
      this.map.dragPan.disable();
      this.roadsCircleDrag = {
        pointerId: e.pointerId,
        samples: [this.unprojectLocal(at)],
        x: at.x,
        y: at.y,
      };
      this.paintRoadsBrush();
      return;
    }
    {
      const at = this.clientToLocal(e.clientX, e.clientY);
      const canvasHandle = this.hitCanvasHandle(at.x, at.y);
      if (canvasHandle && this.wantsCanvasEdit(e, canvasHandle)) {
        if (!e.isPrimary || !this.workArea) return;
        this.takePointer(e);
        this.map.dragPan.disable();
        const center = this.map.project(canvasCenter(this.workArea));
        this.canvasEditDrag = {
          pointerId: e.pointerId,
          handle: canvasHandle,
          start: { ...this.workArea },
          origin: at,
          centerScreen: { x: center.x, y: center.y },
          startAngle: Math.atan2(at.x - center.x, center.y - at.y),
          startBearing: canvasBearing(this.workArea),
          originLl: this.unprojectLocal(at),
          moved: false,
        };
        return;
      }
    }
    if (this.wantsWorkRect(e)) {
      if (!e.isPrimary) return;
      const at = this.clientToLocal(e.clientX, e.clientY);
      this.takePointer(e);
      this.map.dragPan.disable();
      this.workDrag = {
        pointerId: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        origin: at,
        moved: false,
        bearing: this.map.getBearing(),
      };
      return;
    }
    const at = this.clientToLocal(e.clientX, e.clientY);
    const touch = e.pointerType === "touch";
    if (this.mode === "erase") {
      // A second finger is left for pinch-zoom. One finger deletes, so it
      // must not pan the map out from under the stroke.
      if (!e.isPrimary) return;
      if (!touch && !this.usesButton("eraseStroke", e.button)) return;
      this.takePointer(e);
      this.map.dragPan.disable();
      this.eraseDrag = {
        pointerId: e.pointerId,
        before: this.snapshot(),
        erased: false,
        x: at.x,
        y: at.y,
        stroke: beginEraseStroke(this.points.length, this.segments),
      };
      this.touchErase(at.x, at.y, true);
      return;
    }
    if (this.mode === "boundary") {
      if (!touch && !this.usesButton("moveBoundary", e.button)) return;
      const vertex = this.hitBoundaryVertex(at.x, at.y, this.segmentHitPx(e.pointerType));
      if (vertex == null) return;
      this.takePointer(e);
      this.map.dragPan.disable();
      this.boundaryDrag = {
        pointerId: e.pointerId,
        index: vertex,
        x: e.clientX,
        y: e.clientY,
        moved: false,
      };
      return;
    }
    const selecting = this.pointerHits("select", "click", e);
    const toggling = this.pointerHits("toggleSelect", "click", e);
    const picks = selecting || toggling || this.usesButton("select", e.button);
    const movesPoint = touch || this.usesButton("movePoint", e.button);
    const isolated = selectToolIsolates(this.selectionTool);
    const vertex = this.hitVertex(at.x, at.y);
    const segment = this.hitSegment(at.x, at.y, this.segmentHitPx(e.pointerType));
    const hit = selectableHit(this.showPoints, vertex, segment);
    if (!isolated && hit.kind === "point" && (movesPoint || (fine && picks))) {
      this.takePointer(e);
      this.map.dragPan.disable();
      this.vertexDrag = {
        pointerId: e.pointerId,
        index: hit.index,
        x: e.clientX,
        y: e.clientY,
        before: this.snapshot(),
        moved: false,
        fine,
        additive: toggling,
        picks: fine && picks,
        moves: movesPoint,
      };
      return;
    }

    // Touch taps add from the map click. Mouse and pen select.
    if (!fine) return;
    if (!isolated && hit.kind === "line" && (picks || movesPoint)) {
      this.takePointer(e);
      this.map.dragPan.disable();
      this.segmentDrag = {
        pointerId: e.pointerId,
        segment: hit.index,
        x: e.clientX,
        y: e.clientY,
        moved: false,
        additive: toggling,
      };
      return;
    }
    if (hit.kind === "empty" && this.hitCanvasBody(at.x, at.y) && this.wantsCanvasEdit(e, "body") && this.workArea) {
      this.takePointer(e);
      this.map.dragPan.disable();
      const center = this.map.project(canvasCenter(this.workArea));
      this.canvasEditDrag = {
        pointerId: e.pointerId,
        handle: "body",
        start: { ...this.workArea },
        origin: at,
        centerScreen: { x: center.x, y: center.y },
        startAngle: 0,
        startBearing: canvasBearing(this.workArea),
        originLl: this.unprojectLocal(at),
        moved: false,
      };
      return;
    }
    // Box, Circle, and Lasso do not pick or drag a point. A drag that starts
    // on a point or line still runs the tool once it passes the threshold.
    // A click there does nothing. A plain click on empty map clears, and
    // Shift or Ctrl/Cmd does not.
    const onFeature = hit.kind !== "empty";
    const lasso = isolated ? this.lassoMode(e) : null;
    const clears = emptyClickClears(this.selectionTool, {
      selecting,
      toggling,
      selectButton: this.usesButton("select", e.button),
      shift: e.shiftKey,
      ctrl: e.ctrlKey,
      meta: e.metaKey,
      onFeature,
    });
    if (
      !beginSelectGesture({
        tool: this.selectionTool,
        onFeature,
        mode: lasso,
        clears,
        selecting,
        toggling,
      })
    ) {
      return;
    }
    this.lassoDrag = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      moved: false,
      mode: lasso,
      ring: [at],
      additive: toggling,
      clears,
      tool: this.selectionTool,
      hits: [],
      hitMark: null,
      lineMark: null,
      replaced: false,
    };
    e.preventDefault();
  };

  private pointerHits(action: ActionId, kind: "click" | "drag", e: PointerEvent): boolean {
    return actionMatchesPointer(this.keymap, action, this.pointerState(e, kind), this.inputPlatform);
  }

  private usesButton(action: ActionId, button: number): boolean {
    return actionUsesButton(this.keymap, action, button);
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (this.gestureOwns(e.pointerId)) e.stopPropagation();
    this.queueMove({
      pointerId: e.pointerId,
      clientX: e.clientX,
      clientY: e.clientY,
      alt: e.altKey,
      shift: e.shiftKey,
      ctrl: e.ctrlKey,
      meta: e.metaKey,
      overMap: e.target instanceof Node && this.surface.contains(e.target),
    });
  };

  private onCanvasMouseMove = (e: MouseEvent): void => {
    if (e.buttons !== 0) return;
    this.queueMove({
      pointerId: -1,
      clientX: e.clientX,
      clientY: e.clientY,
      alt: e.altKey,
      shift: e.shiftKey,
      ctrl: e.ctrlKey,
      meta: e.metaKey,
      overMap: true,
    });
  };

  /** Keep the latest sample and run the gesture at most once per frame. */
  private queueMove(sample: MoveSample): void {
    this.moveSample = sample;
    if (this.moveQueued) return;
    this.moveQueued = true;
    requestAnimationFrame(() => {
      this.moveQueued = false;
      const pending = this.moveSample;
      this.moveSample = null;
      if (pending) this.flushMove(pending);
    });
  }

  private flushPendingMove(pointerId: number): void {
    const sample = this.moveSample;
    if (!sample || sample.pointerId !== pointerId) return;
    this.moveSample = null;
    this.flushMove(sample);
  }

  private flushMove(sample: MoveSample): void {
    this.pointerAt = { x: sample.clientX, y: sample.clientY };
    this.pointerOverMap = sample.overMap;
    if (sample.overMap) {
      this.cursor = this.clientToLocal(sample.clientX, sample.clientY);
      this.syncMods({
        altKey: sample.alt,
        shiftKey: sample.shift,
        ctrlKey: sample.ctrl,
        metaKey: sample.meta,
      });
    }
    if (this.grab) {
      if (sample.overMap) this.previewGrab();
      this.refreshCursor();
      return;
    }
    const consumed = sample.pointerId >= 0 && this.applyGesture(sample);
    if (!this.viewDrag) {
      if (this.mode === "erase") this.paintEraseBrush();
      else if (this.mode === "roadsCircle") this.paintRoadsBrush();
      else this.refreshSelectCircle();
    }
    if (consumed) return;
    if (sample.overMap) this.refreshCursor();
  }

  /**
   * Rotate and pan update the live transform and schedule one render.
   * `jumpTo` / `panBy` fire `moveend` on every frame, and that reloads the
   * cached-piece overlay. The gesture settles with one `jumpTo` on release.
   */
  private nudgeView(action: ViewDrag["action"], dx: number, dy: number): void {
    try {
      if (this.camera.programmaticMotionActive()) return;
      if (this.map.isMoving() || this.map.isEasing()) return;
      const tr = this.map.transform as unknown as {
        bearing: number;
        pitch: number;
        center: maplibregl.LngLat;
        centerPoint: maplibregl.Point;
        setBearing(bearing: number): void;
        setPitch(pitch: number): void;
        setLocationAtPoint(lnglat: maplibregl.LngLat, point: maplibregl.Point): void;
      };
      if (!finiteMapCenter(tr.center)) return;
      if (action === "rotateView") {
        this.northMemory = null;
        const next = applyViewRotate(tr.bearing, tr.pitch, dx, dy);
        tr.setBearing(next.bearing);
        tr.setPitch(next.pitch);
      } else {
        tr.setLocationAtPoint(tr.center, tr.centerPoint.add(new maplibregl.Point(dx, dy)));
      }
      this.invalidateScreenGrid();
      if (!finiteMapCenter(this.safeMapCenter())) return;
      if (this.viewDrag) this.viewDrag.nudged = true;
      deferAfterMapFrame(() => {
        try {
          if (!this.viewDrag) return;
          if (this.camera.programmaticMotionActive()) return;
          if (this.map.isMoving() || this.map.isEasing()) return;
          this.map.triggerRepaint();
          if (action === "rotateView") this.paintViewControls();
        } catch {
          /* never throw from inside MapLibre's render task */
        }
      });
    } catch {
      /* manual pan must never throw — a bad frame should not freeze the map */
    }
  }

  private safeMapCenter(): maplibregl.LngLat | null {
    try {
      const center = this.map.getCenter();
      return finiteMapCenter(center) ? center : null;
    } catch {
      return null;
    }
  }

  /** Publish the camera once so listeners see a single moveend. */
  private settleView(): void {
    const whenReady = (): void => {
      if (this.camera.programmaticMotionActive() || this.map.isMoving() || this.map.isEasing()) {
        this.map.once("moveend", () => deferAfterMapFrame(whenReady));
        return;
      }
      this.camera.snapView();
    };
    deferAfterMapFrame(whenReady);
  }

  private applyGesture(sample: MoveSample): boolean {
    const pointerId = sample.pointerId;
    if (this.placeClick && pointerId === this.placeClick.pointerId) {
      if (Math.hypot(sample.clientX - this.placeClick.x, sample.clientY - this.placeClick.y) >= 4) {
        this.placeClick.moved = true;
      }
      return true;
    }
    if (this.roadsBoxDrag && pointerId === this.roadsBoxDrag.pointerId) {
      const drag = this.roadsBoxDrag;
      const at = this.clientToLocal(sample.clientX, sample.clientY);
      if (!drag.moved) {
        if (Math.hypot(sample.clientX - drag.x, sample.clientY - drag.y) < LASSO_DRAG_PX) return true;
        drag.moved = true;
      }
      const origin = drag.origin;
      this.drawSelectBox(origin, at);
      return true;
    }
    if (this.roadsCircleDrag && pointerId === this.roadsCircleDrag.pointerId) {
      const drag = this.roadsCircleDrag;
      const at = this.clientToLocal(sample.clientX, sample.clientY);
      if (Math.hypot(at.x - drag.x, at.y - drag.y) >= 2) {
        const prev = drag.samples[drag.samples.length - 1];
        const next = this.unprojectLocal(at);
        drag.samples.push(next);
        drag.lines ??= this.roadLines();
        const r = this.roadsBrushRadiusM(next);
        const piece = clipLinesToStroke(drag.lines, prev ? [prev, next] : [next], r);
        (drag.preview ??= []).push(...piece);
        drag.x = at.x;
        drag.y = at.y;
      }
      this.paintRoadsBrush();
      return true;
    }
    if (this.workDrag && pointerId === this.workDrag.pointerId) {
      const drag = this.workDrag;
      if (!drag.moved && Math.hypot(sample.clientX - drag.x, sample.clientY - drag.y) < WORK_DRAG_PX) return true;
      const at = this.clientToLocal(sample.clientX, sample.clientY);
      if (!drag.moved) {
        drag.moved = true;
        this.renderWorkArea();
      }
      this.workPreview = normalizeWorkArea(
        this.unprojectLocal(drag.origin),
        this.unprojectLocal(at),
        drag.bearing,
      );
      if (this.workPreview) this.drawWorkPreview(this.workPreview);
      return true;
    }
    if (this.canvasEditDrag && pointerId === this.canvasEditDrag.pointerId) {
      const drag = this.canvasEditDrag;
      const at = this.clientToLocal(sample.clientX, sample.clientY);
      if (!drag.moved) {
        if (Math.hypot(sample.clientX - drag.origin.x, sample.clientY - drag.origin.y) < WORK_DRAG_PX) return true;
        drag.moved = true;
      }
      const next = this.canvasFromEdit(drag, at);
      if (next) {
        this.workPreview = next;
        this.drawWorkPreview(next);
      }
      return true;
    }
    if (this.lassoDrag && pointerId === this.lassoDrag.pointerId) {
      const drag = this.lassoDrag;
      const at = this.clientToLocal(sample.clientX, sample.clientY);
      if (!drag.moved) {
        if (Math.hypot(sample.clientX - drag.x, sample.clientY - drag.y) < LASSO_DRAG_PX) return true;
        drag.moved = true;
        if (drag.mode) this.claimPointer(pointerId);
        this.refreshCursor();
      }
      if (!drag.mode) return true;
      if (drag.tool === "box") {
        const origin = drag.ring[0];
        if (origin) this.drawSelectBox(origin, at);
        return true;
      }
      if (drag.tool === "circle") {
        this.paintCircleLive(drag, at);
        this.paintSelectCircleAt(at.x, at.y);
        return true;
      }
      const last = drag.ring[drag.ring.length - 1];
      if (!last || Math.hypot(at.x - last.x, at.y - last.y) >= 2) drag.ring.push(at);
      this.drawLasso(drag.ring);
      return true;
    }
    if (this.viewDrag && pointerId === this.viewDrag.pointerId) {
      const drag = this.viewDrag;
      const dx = sample.clientX - drag.lastX;
      const dy = sample.clientY - drag.lastY;
      if (!drag.moved) {
        if (Math.hypot(sample.clientX - drag.x, sample.clientY - drag.y) < 4) return true;
        drag.moved = true;
        if (this.selectCircleShown) this.clearOverlay();
        if (this.map.isMoving() || this.map.isEasing()) {
          const pointerId = drag.pointerId;
          this.camera.armViewNudge(() => {
            drag.nudgeOk = true;
            this.flushPendingMove(pointerId);
          });
          return true;
        }
        drag.nudgeOk = true;
      }
      if (!drag.nudgeOk) return true;
      this.nudgeView(drag.action, dx, dy);
      drag.lastX = sample.clientX;
      drag.lastY = sample.clientY;
      return true;
    }
    if (this.eraseDrag && pointerId === this.eraseDrag.pointerId) {
      const at = this.clientToLocal(sample.clientX, sample.clientY);
      this.touchErase(at.x, at.y, false);
      return true;
    }
    if (this.boundaryDrag && pointerId === this.boundaryDrag.pointerId) {
      if (!this.boundaryDrag.moved) {
        if (Math.hypot(sample.clientX - this.boundaryDrag.x, sample.clientY - this.boundaryDrag.y) < 6) return true;
        this.boundaryDrag.moved = true;
      }
      const at = this.clientToLocal(sample.clientX, sample.clientY);
      this.boundary[this.boundaryDrag.index] = this.unprojectLocal(at);
      this.renderBoundary();
      return true;
    }
    if (this.vertexDrag && pointerId === this.vertexDrag.pointerId) {
      if (!this.vertexDrag.moves) {
        if (Math.hypot(sample.clientX - this.vertexDrag.x, sample.clientY - this.vertexDrag.y) >= 6) {
          this.vertexDrag.moved = true;
        }
        return true;
      }
      if (!this.vertexDrag.moved) {
        if (Math.hypot(sample.clientX - this.vertexDrag.x, sample.clientY - this.vertexDrag.y) < 6) return true;
        this.vertexDrag.moved = true;
        this.selectOnly(this.vertexDrag.index);
        this.hideIssuesUntilChecked();
      }
      const at = this.clientToLocal(sample.clientX, sample.clientY);
      const next = this.unprojectLocal(at);
      this.points[this.vertexDrag.index] = this.dragSnap(this.vertexDrag, at, next);
      this.renderDraft();
      return true;
    }
    if (!this.segmentDrag || pointerId !== this.segmentDrag.pointerId) return false;
    if (!this.segmentDrag.moved) {
      if (Math.hypot(sample.clientX - this.segmentDrag.x, sample.clientY - this.segmentDrag.y) < 6) return true;
      this.segmentDrag.moved = true;
    }
    return true;
  }

  private gestureOwns(pointerId: number): boolean {
    return (
      this.placeClick?.pointerId === pointerId ||
      this.workDrag?.pointerId === pointerId ||
      this.canvasEditDrag?.pointerId === pointerId ||
      this.roadsBoxDrag?.pointerId === pointerId ||
      this.roadsCircleDrag?.pointerId === pointerId ||
      this.lassoDrag?.pointerId === pointerId ||
      this.viewDrag?.pointerId === pointerId ||
      this.eraseDrag?.pointerId === pointerId ||
      this.boundaryDrag?.pointerId === pointerId ||
      this.vertexDrag?.pointerId === pointerId ||
      this.segmentDrag?.pointerId === pointerId
    );
  }

  private onPointerUp = (e: PointerEvent): void => {
    try {
      this.onPointerUpInner(e);
    } catch {
      this.viewDrag = null;
      this.releaseDragPan();
      this.refreshCursor();
    }
  };

  private onPointerUpInner(e: PointerEvent): void {
    this.flushPendingMove(e.pointerId);
    this.syncMods(e);
    if (this.roadsBoxDrag && e.pointerId === this.roadsBoxDrag.pointerId) {
      const drag = this.roadsBoxDrag;
      this.roadsBoxDrag = null;
      const at = this.clientToLocal(e.clientX, e.clientY);
      this.clearOverlay();
      this.armIgnoreClick();
      this.releaseDragPan();
      if (drag.moved && screenRectReady(drag.origin, at, LASSO_DRAG_PX)) {
        const ring = screenRectCorners(drag.origin, at).map((corner) => this.unprojectLocal(corner));
        this.fillRoadsInRing(ring);
      }
      return;
    }
    if (this.roadsCircleDrag && e.pointerId === this.roadsCircleDrag.pointerId) {
      const drag = this.roadsCircleDrag;
      this.roadsCircleDrag = null;
      this.armIgnoreClick();
      this.releaseDragPan();
      const radiusM = this.roadsBrushRadiusM(drag.samples[0] ?? this.unprojectLocal({ x: drag.x, y: drag.y }));
      this.fillRoadsAlongStroke(drag.samples, radiusM);
      this.paintRoadsBrush();
      return;
    }
    if (this.workDrag && e.pointerId === this.workDrag.pointerId) {
      const drag = this.workDrag;
      const area = drag.moved ? this.workPreview : null;
      this.workDrag = null;
      this.workPreview = null;
      this.clearOverlay();
      this.armIgnoreClick();
      this.releaseDragPan();
      if (!area) {
        this.renderWorkArea();
        return;
      }
      this.commitWorkArea(area);
      return;
    }
    if (this.canvasEditDrag && e.pointerId === this.canvasEditDrag.pointerId) {
      const drag = this.canvasEditDrag;
      const area = drag.moved ? this.workPreview : null;
      this.canvasEditDrag = null;
      this.workPreview = null;
      this.clearOverlay();
      this.armIgnoreClick();
      this.releaseDragPan();
      if (!area) {
        this.renderWorkArea();
        return;
      }
      this.commitWorkArea(area);
      return;
    }
    if (this.placeClick && e.pointerId === this.placeClick.pointerId) {
      const click = this.placeClick;
      this.placeClick = null;
      if (!click.moved) {
        const at = this.clientToLocal(click.x, click.y);
        this.editAt(at.x, at.y);
      }
      e.preventDefault();
      return;
    }
    if (this.lassoDrag && e.pointerId === this.lassoDrag.pointerId) {
      const drag = this.lassoDrag;
      this.lassoDrag = null;
      this.clearOverlay();
      this.refreshCursor();
      const release = selectPressRelease(drag.moved, drag.clears && !drag.additive, drag.mode);
      if (release === "clear") this.assignClick({ kind: "empty" }, false);
      if (release !== "apply" || drag.mode == null) {
        this.refreshSelectCircle();
        if (drag.moved || drag.clears) this.armIgnoreClick();
        return;
      }
      this.armIgnoreClick();
      if (drag.tool === "circle") {
        this.refreshSelectCircle();
        this.notify(false);
        return;
      }
      this.applyShapeSelection(drag, e);
      this.renderDraft();
      this.notify(false);
      this.refreshSelectCircle();
      return;
    }
    if (this.viewDrag && e.pointerId === this.viewDrag.pointerId) {
      const drag = this.viewDrag;
      this.viewDrag = null;
      if (drag.moved && (drag.nudgeOk || drag.nudged)) {
        if (drag.nudgeOk) this.settleView();
        this.onBrowseMapUserChanged?.();
      }
      this.refreshCursor();
      this.armIgnoreClick();
      this.releaseDragPan();
      if (!drag.moved && this.browseMode && this.ready) {
        const rect = this.map.getCanvas().getBoundingClientRect();
        const point = [drag.x - rect.left, drag.y - rect.top] as maplibregl.PointLike;
        if (this.pickNearbyAt(point)) return;
        if (!this.pickHomeQuestRouteAt(point)) this.onBrowseMapBackgroundClick?.();
        return;
      }
      if (!drag.moved && this.mode === "graph") {
        const click = matchPointer(this.keymap, this.pointerState(e, "click"), this.inputPlatform);
        if (click === "deletePointAtCursor") {
          const at = this.clientToLocal(drag.x, drag.y);
          const vertex = this.hitVertex(at.x, at.y);
          if (vertex != null) this.deleteAt(vertex);
        }
      }
      return;
    }
    if (this.eraseDrag && e.pointerId === this.eraseDrag.pointerId) {
      const drag = this.eraseDrag;
      this.eraseDrag = null;
      this.armIgnoreClick();
      this.releaseDragPan();
      if (!drag.erased) return;
      void this.commitEraseStroke(drag);
      return;
    }
    if (this.boundaryDrag && e.pointerId === this.boundaryDrag.pointerId) {
      const drag = this.boundaryDrag;
      this.boundaryDrag = null;
      this.armIgnoreClick();
      this.releaseDragPan();
      if (!drag.moved && drag.index === 0 && this.canCloseBoundary()) this.closeBoundary();
      return;
    }
    if (this.vertexDrag && e.pointerId === this.vertexDrag.pointerId) {
      const drag = this.vertexDrag;
      this.vertexDrag = null;
      this.armIgnoreClick();
      this.releaseDragPan();
      if (drag.moved) {
        if (!drag.moves) return;
        this.clearSnapMark();
        if (drag.snap && this.joinDrop(drag, drag.snap)) return;
        this.past.push(drag.before);
        if (this.past.length > HISTORY_LIMIT) this.past.shift();
        this.future = [];
        this.routeTick += 1;
        this.selectOnly(drag.index);
        this.renderDraft();
        this.notify(true);
        return;
      }
      if (drag.fine) {
        if (drag.picks) this.assignClick({ kind: "point", index: drag.index }, drag.additive);
      } else this.activateVertex(drag.index);
      return;
    }

    if (!this.segmentDrag || e.pointerId !== this.segmentDrag.pointerId) return;
    const drag = this.segmentDrag;
    this.segmentDrag = null;
    this.armIgnoreClick();
    this.releaseDragPan();
    if (drag.moved) return;
    this.assignClick({ kind: "line", index: drag.segment }, drag.additive);
  }

  private commit(mutate: () => void): void {
    if (this.grab) this.cancelGrab();
    const before = this.snapshot();
    mutate();
    const joinOrder = this.pendingJoinOrder;
    const collapse = this.pendingCollapse;
    this.reconcileStyles(before);
    this.reconcileDecorative(before, joinOrder, collapse);
    this.syncRegionsNow();
    if (sameSnapshot(before, this.snapshot())) {
      this.pendingPointMap = null;
      return;
    }
    this.past.push(before);
    if (this.past.length > HISTORY_LIMIT) this.past.shift();
    this.future = [];
    this.routeTick += 1;
    this.renderDraft();
    this.notify(true);
  }

  private snapshot(): Snapshot {
    return {
      workArea: this.workArea ? { ...this.workArea } : null,
      points: this.points.map(clone),
      segments: this.segments.map(([a, b]) => [a, b]),
      start: this.start,
      selection: this.selection.slice(),
      selectedLines: this.selectedLines.slice(),
      clickOrder: this.clickOrder ? this.clickOrder.slice() : null,
      orderAnchor: this.orderAnchor,
      spacing: this.spacing,
      fill: cloneFill(this.fill),
      regions: cloneRegions(this.regions),
      pointStyles: clonePointStyles(this.pointStyles, this.points.length),
      lineStyles: cloneLineStyles(this.lineStyles, this.segments.length),
      decorative: cloneDecorative(this.decorative, this.segments.length),
      styleDefaults: parseStyleDefaults(this.nextStyleDefaults),
      pieceStyleLocked: this.pieceStyleLocked,
    };
  }

  private reconcileDecorative(
    before: Snapshot,
    joinOrder: readonly number[] | null,
    collapse: readonly (readonly number[])[] | null,
  ): void {
    if (this.sameDrawnTopology(before)) {
      if (this.decorative.length !== this.segments.length) {
        this.decorative = cloneDecorative(this.decorative, this.segments.length);
      }
      return;
    }
    const pointMap = this.pendingPointMap ?? matchPointsForStyles(before.points, this.points);
    if (joinOrder) {
      this.decorative = joinedDecorative(before.segments, before.decorative, this.segments, joinOrder);
    } else if (collapse) {
      this.decorative = dissolvedDecorative(
        before.decorative,
        before.segments,
        this.segments,
        pointMap,
        collapse,
      );
    } else {
      this.decorative = remapDecorative(before.decorative, before.segments, this.segments, pointMap);
    }
  }

  /**
   * After a topology edit, surviving features keep their style.
   * A join copies a line already on the earlier endpoint, then the later one,
   * and copies the piece default when neither has a style. A dissolve bridge copies the
   * removed lines (the style they share, otherwise the earliest). Other new
   * lines copy the Settings default. A style-only edit is left as written.
   */
  private reconcileStyles(before: Snapshot): void {
    const joinOrder = this.pendingJoinOrder;
    const collapse = this.pendingCollapse;
    this.pendingJoinOrder = null;
    this.pendingCollapse = null;
    if (this.sameDrawnTopology(before)) {
      if (this.pointStyles.length !== this.points.length) {
        this.pointStyles = clonePointStyles(this.pointStyles, this.points.length);
      }
      if (this.lineStyles.length !== this.segments.length) {
        this.lineStyles = cloneLineStyles(this.lineStyles, this.segments.length);
      }
      return;
    }
    const pointMap = this.pendingPointMap ?? matchPointsForStyles(before.points, this.points);
    this.pointStyles = remapPointStyles(before.pointStyles, pointMap, this.points.length, this.nextStyleDefaults.point);
    if (joinOrder) {
      this.lineStyles = joinedLineStyles(
        before.segments,
        before.lineStyles,
        this.segments,
        joinOrder,
        this.nextStyleDefaults.line,
      );
    } else if (collapse) {
      this.lineStyles = dissolvedLineStyles(before.lineStyles, before.segments, this.segments, pointMap, collapse);
    } else {
      this.lineStyles = remapLineStyles(
        before.lineStyles,
        before.segments,
        this.segments,
        pointMap,
        this.nextStyleDefaults.line,
      );
    }
  }

  private sameDrawnTopology(before: Snapshot): boolean {
    if (before.points.length !== this.points.length || before.segments.length !== this.segments.length) return false;
    for (let i = 0; i < before.segments.length; i++) {
      const prev = before.segments[i];
      const next = this.segments[i];
      if (!prev || !next || prev[0] !== next[0] || prev[1] !== next[1]) return false;
    }
    return true;
  }

  private applySnapshot(s: Snapshot): void {
    this.points = s.points.map(clone);
    this.segments = s.segments.map(([a, b]) => [a, b]);
    this.pointStyles = clonePointStyles(s.pointStyles, s.points.length);
    this.lineStyles = cloneLineStyles(s.lineStyles, s.segments.length);
    this.decorative = cloneDecorative(s.decorative, s.segments.length);
    this.nextStyleDefaults = parseStyleDefaults(s.styleDefaults);
    this.pieceStyleLocked = s.pieceStyleLocked;
    this.start = s.start;
    this.selection = s.selection.slice();
    this.selectedLines = s.selectedLines.slice();
    this.breakSelectAllCycle();
    this.clickOrder = s.clickOrder ? s.clickOrder.slice() : null;
    this.orderAnchor = s.orderAnchor;
    this.spacing = s.spacing;
    this.fill = cloneFill(s.fill);
    this.regions = cloneRegions(s.regions);
    this.regionKnown = false;
    this.regionLayer.invalidate();
    this.routeTick += 1;
    if (!sameWorkArea(this.workArea, s.workArea)) this.applyWorkArea(s.workArea ? { ...s.workArea } : null);
    this.renderDraft();
  }

  /** Set or clear the canvas without touching history, then read roads again. */
  private applyWorkArea(area: WorkArea | null): void {
    this.workPreview = null;
    this.canvasEditDrag = null;
    this.storeRoads([]);
    window.clearTimeout(this.extractTimer);
    if (!area) {
      this.workArea = null;
      this.roadSourceIds = new Set();
      this.extractWatch = idleExtractWatch();
      this.renderWorkArea();
      return;
    }
    this.workArea = area;
    this.extractWatch = watchForNewArea(area);
    this.renderWorkArea();
    this.readRoadsWhenSettled();
  }

  private pushHistory(before: Snapshot): void {
    if (sameSnapshot(before, this.snapshot())) return;
    this.past.push(before);
    if (this.past.length > HISTORY_LIMIT) this.past.shift();
    this.future = [];
  }

  private writeSpacing(spacing: number): void {
    const memory = this.fill;
    if (!memory) {
      this.spacing = spacing;
      return;
    }
    const before = this.snapshot();
    const resampled = resampleRoadGraph(memory.welded, spacing);
    const stripped = subtractFilledRoads(
      { points: this.points, segments: this.segments, start: this.start },
      memory.basePoints,
      memory.resampled,
      COINCIDE_TOLERANCE_M,
    );
    const merged = mergeGraphs(stripped, resampled.graph, COINCIDE_TOLERANCE_M);
    this.points = merged.points;
    this.segments = merged.segments;
    this.start = merged.start;
    this.reconcileStyles(before);
    this.forgetSelection();
    this.spacing = spacing;
    this.fill = {
      basePoints: memory.basePoints.map(clone),
      welded: cloneGraph(memory.welded),
      resampled: resampled.graph,
      covers: resampled.covers.map((cover) => cover.slice()),
    };
    this.routeTick += 1;
    this.renderDraft();
  }

  /**
   * Compact every line the brush hid during the stroke.
   * The undo snapshot was taken when the pointer went down, so this is one step.
   */
  private async commitEraseStroke(drag: EraseDrag): Promise<void> {
    const removed = removedSegmentIndexes(drag.stroke);
    if (removed.length === 0) return;
    await this.withWaitCursor(eraseCommitIsHeavy(removed.length), () => {
      const before = this.snapshot();
      const beforePoints = this.points.map((point) => [point[0], point[1]] as LngLat);
      const erased = eraseSegments(this.points, this.segments, removed, this.start, null);
      this.points = erased.points;
      this.segments = erased.segments;
      this.start = erased.start;
      this.reconcileStyles(before);
      this.selection = remapSelection(beforePoints, erased.points, this.selection);
      this.selectedLines = [];
      this.noteBulk(this.selection[0] ?? null);
      this.routeTick += 1;
      this.renderDraft();
      this.past.push(drag.before);
      if (this.past.length > HISTORY_LIMIT) this.past.shift();
      this.future = [];
      this.notify(true);
    });
  }

  /** Hide every line under the brush. The graph indexes stay put until release. */
  private touchErase(x: number, y: number, first: boolean): void {
    const drag = this.eraseDrag;
    if (!drag) return;
    const x0 = first ? x : drag.x;
    const y0 = first ? y : drag.y;
    drag.x = x;
    drag.y = y;
    const grid = this.ensureScreenGrid();
    const lineHits = segmentsUnderBrush(grid, x0, y0, x, y, this.eraseRadius, drag.stroke.hidden);
    const pointHits = this.showPoints ? pointsUnderBrush(grid, x0, y0, x, y, this.eraseRadius) : [];
    const hits = eraseTargets(lineHits, pointHits, this.segments, this.showPoints);
    if (hits.length === 0) return;
    const taken = consumeEraseHits(drag.stroke, hits, this.segments);
    if (taken.segments.length === 0) return;
    for (const index of taken.segments) {
      const segment = this.segments[index];
      if (!segment) continue;
      const a = this.points[segment[0]];
      const b = this.points[segment[1]];
      if (a && b) this.exciseFillGeometry(a, b);
    }
    const highlighted = this.selectedLines;
    if (highlighted.length > 0) this.selectedLines = [];
    drag.erased = true;
    const dropped = this.scene.dropSegments(taken.segments, taken.orphans);
    const unpicked = highlighted.length > 0 ? this.scene.unpickLines(highlighted) : null;
    this.pushDraftDiff(mergeDraftDiffs(dropped, unpicked));
  }

  /** Cut a deleted segment out of the welded roads so the slider cannot restore it. */
  private exciseFillGeometry(a: LngLat, b: LngLat): void {
    const memory = this.fill;
    if (!memory) return;
    const match = memory.resampled.segments.findIndex(([i, j]) => {
      const pa = memory.resampled.points[i];
      const pb = memory.resampled.points[j];
      return !!pa && !!pb && endsNear(a, b, pa, pb, COINCIDE_TOLERANCE_M + 2);
    });
    if (match < 0) return;
    const drop = memory.covers[match] ?? [];
    const excised = exciseWeldedEdges(memory.welded, drop);
    const resampled = eraseSegments(memory.resampled.points, memory.resampled.segments, [match], null, null);
    const removed = new Set(drop);
    this.fill = {
      basePoints: memory.basePoints.map(clone),
      welded: excised.graph,
      resampled: { points: resampled.points, segments: resampled.segments, start: null },
      covers: memory.covers.flatMap((cover, index) => {
        if (index === match) return [];
        return [
          cover.flatMap((edge) => {
            if (removed.has(edge)) return [];
            const next = excised.segmentMap[edge];
            return next == null || next < 0 ? [] : [next];
          }),
        ];
      }),
    };
  }

  /** Swallow the click that follows a pointer gesture, then forget it. */
  private armIgnoreClick(): void {
    this.ignoreClick = true;
    window.setTimeout(() => {
      this.ignoreClick = false;
    }, 0);
  }

  /** Re-enable touch pan. Mouse left-drag stays a lasso, not a pan. */
  private releaseDragPan(): void {
    this.restoreTouchPan();
    this.map.touchZoomRotate.enable();
  }

  private restoreTouchPan(): void {
    if (this.mode === "erase" || this.mode === "work" || this.mode === "roadsBox" || this.mode === "roadsCircle")
      return;
    this.map.dragPan.enable();
    this.suppressMousePan();
  }

  private suppressMousePan(): void {
    const pan = this.map.dragPan as unknown as { _mousePan?: { disable(): void } };
    pan._mousePan?.disable();
  }

  private lassoMode(e: PointerEvent): LassoMode | null {
    const action = matchPointer(this.keymap, this.pointerState(e, "drag"), this.inputPlatform);
    if (action === "lassoSelect") return "replace";
    if (action === "lassoAdd") return "add";
    if (action === "lassoSubtract") return "subtract";
    return null;
  }

  /** Drop the brush circle. The wheel keeps zooming the map. */
  private leaveErase(): void {
    if (this.mode !== "erase") return;
    this.clearOverlay();
  }

  /** Keyboard zoom. One step matches the old map shortcut; Shift used two. */
  zoomBy(steps: number): void {
    this.camera.zoomBy(steps);
  }

  /** Await every queued programmatic camera move (live harnesses). */
  cameraWhenIdle(): Promise<void> {
    return this.camera.whenIdle();
  }

  cameraProgrammaticActive(): boolean {
    return this.camera.programmaticMotionActive();
  }

  /**
   * North (N key and the compass). Rotate the map so north is up, keeping the pitch.
   * Pressed again before the user turns the map, it goes back to the bearing and pitch
   * it left. Already north with nothing to go back to and a selected quest drawn on a
   * turned canvas: turn to that canvas bearing, like the quest page compass.
   */
  northUp(): CompassStep["kind"] {
    const step = compassToggle(this.currentView(), this.northMemory, this.questViewBearing);
    this.northMemory = step.memory;
    if (step.kind === "none") return step.kind;
    this.camera.turnTo(step.target.bearing, step.target.pitch, COMPASS_TURN_MS);
    this.northTarget = this.map.isEasing() ? step.target : null;
    this.paintViewControls();
    return step.kind;
  }

  /** Panels over the map, in px from each edge, kept clear when framing a quest. */
  setViewInsets(insets: () => Insets): void {
    this.camera.setInsets(insets);
  }

  /** Corner overlays measured at fit time. Edge-spanning panels stay in {@link setViewInsets}. */
  setViewObstacles(obstacles: () => ScreenRect[]): void {
    this.camera.setObstacles(obstacles);
  }

  viewInsetsNow(): Insets {
    return this.camera.insetsNow();
  }

  private questFitLine(): LngLat[] | null {
    if (this.questViewRoute && this.questViewRoute.length >= 2) return this.questViewRoute;
    if (this.questViewSpots?.length) return this.homeQuestSpots.coordinates(this.questViewSpots);
    return null;
  }

  setHomeQuestSpotSelection(index: number | null): void {
    this.homeQuestSpotSelectedIndex = index;
    if (this.questViewSpots?.length) this.homeQuestSpots.setSelectedIndex(index);
  }

  private homeQuestSpotShowOpts(): { selectedIndex: number | null; onSelect: (index: number) => void } {
    return {
      selectedIndex: this.homeQuestSpotSelectedIndex,
      onSelect: (index) => this.onHomeQuestSpotSelect?.(index),
    };
  }

  private syncHomeQuestSpots(): void {
    if (this.questViewSpots?.length) this.homeQuestSpots.show(this.questViewSpots, this.homeQuestSpotShowOpts());
    else {
      this.homeQuestSpots.clear();
      this.homeQuestSpotSelectedIndex = null;
    }
  }

  /**
   * The quest selected on the home map: its route and canvas bearing, or null for none.
   * Returns true when that changed the start view.
   */
  setQuestView(
    route: readonly LngLat[] | null,
    canvasBearing: number,
    paint?: readonly RoutePaintFeature[] | null,
    fill?: readonly RouteFillFeature[] | null,
    spots?: readonly HomeSpotPin[] | null,
    routeCanvas?: { west: number; south: number; east: number; north: number } | null,
    deferLayers = false,
  ): boolean {
    this.homeMapLayerGen += 1;
    const line = route && route.length >= 2 ? route.map((p) => [p[0], p[1]] as LngLat) : null;
    const spotList = !line && spots && spots.length > 0 ? spots.map((s) => ({ ...s })) : null;
    const prevSpots = this.questViewSpots;
    if (JSON.stringify(spotList) !== JSON.stringify(prevSpots)) this.homeQuestSpotSelectedIndex = null;
    this.questViewSpots = spotList;
    this.questLayersDeferred = deferLayers;
    if (spotList && !deferLayers) this.syncHomeQuestSpots();
    else this.homeQuestSpots.clear();
    const fitLine = line
      ? (() => {
          const pts = routeHomeMapFitPoints(line, paint ?? null, fill ?? null, routeCanvas ?? null);
          return pts.length >= 2 ? pts : line;
        })()
      : spotList
        ? this.homeQuestSpots.coordinates(spotList)
        : null;
    const bearing = line ? canvasBearing : 0;
    const changed =
      bearing !== this.questViewBearing ||
      JSON.stringify(line) !== JSON.stringify(this.questViewRoute) ||
      JSON.stringify(spotList) !== JSON.stringify(prevSpots);
    this.questViewRoute = line;
    this.questViewBearing = bearing;
    this.questRoutePaint = line ? (paint ?? null) : null;
    this.questRouteFill = line ? (fill ?? null) : null;
    this.camera.setQuestRoute(fitLine && fitLine.length > 0 ? fitLine : null, changed, line ? (paint ?? null) : null);
    if (!deferLayers) this.applyQuestRouteStyles();
    if (!fitLine?.length) this.clearQuestFocus();
    if (changed) this.northMemory = null;
    this.paintViewControls();
    return changed;
  }

  /** Whether the home map should re-fit the selected quest when the canvas size changes. */
  questViewIsFramed(): boolean {
    return this.camera.isFramed();
  }

  setQuestViewFramed(framed: boolean): void {
    this.camera.setFramed(framed);
  }

  /**
   * Point the home map at a selected quest without fitting its route: optionally ease (current zoom)
   * so its start sits mid clear-viewport, then raise the start marker once the camera is still.
   * A newer selection cancels this one.
   */
  focusQuestStart(
    id: string,
    at: LngLat | null,
    opts: { pan: boolean; delayMs?: number; view?: { bearing: number; pitch: number } },
  ): void {
    if (!at) {
      this.clearQuestFocus();
      return;
    }
    const mark = (): void => this.highlight.show(id, at);
    if (opts.pan) this.camera.revealPoint(at, { delayMs: opts.delayMs, onArrive: mark, view: opts.view });
    else {
      this.camera.cancelReveal();
      mark();
    }
  }

  /** Forget a pending list-card reveal and drop the start marker. */
  clearQuestFocus(): void {
    this.camera.cancelReveal();
    this.highlight.clear();
  }

  /** Saved quest line/fill on the home map (same layers as the quest page). */
  private applyQuestRouteStyles(): void {
    if (!this.ready) return;
    const gen = this.homeMapLayerGen;
    const paint = (): void => {
      if (gen !== this.homeMapLayerGen || !mapHasStyleObject(this.map)) return;
      if (!mapStyleReady(this.map)) {
        this.map.once("idle", paint);
        return;
      }
      try {
        this.ensureLayers();
        this.questRouteOnMap = setHomeQuestRoute(this.map, this.questViewRoute, this.questRoutePaint, this.questRouteFill);
        this.applyBrowseLayers();
        this.basemap.reconcileLayerStack();
        if (this.questViewSpots?.length) this.syncHomeQuestSpots();
      } catch {
        /* style torn down mid-quest paint */
      }
    };
    whenMapHasStyle(this.map, paint);
  }

  /**
   * Draw the deferred quest route and spot markers on the frame after the ease's first paint,
   * so building that geometry cannot delay the first camera frame.
   */
  flushDeferredQuestLayers(): void {
    if (!this.questLayersDeferred) return;
    this.questLayersDeferred = false;
    const gen = ++this.questLayerFlushGen;
    const layerGen = this.homeMapLayerGen;
    const apply = (): void => {
      if (gen !== this.questLayerFlushGen || layerGen !== this.homeMapLayerGen) return;
      if (this.questViewSpots?.length) this.syncHomeQuestSpots();
      this.applyQuestRouteStyles();
    };
    // A rAF here still runs before the next paint and can swallow the ease's frames.
    if (this.map.isEasing() || this.map.isMoving()) this.map.once("moveend", () => setTimeout(apply, 0));
    else setTimeout(apply, 0);
  }

  /** Animate to the selected quest's start view: its route or spots fitted at the canvas bearing. */
  showQuestStart(duration = QUEST_VIEW_MS): void {
    const fitLine = this.questFitLine();
    if (!fitLine?.length) return;
    this.questStartFitGen += 1;
    const gen = this.questStartFitGen;
    this.clearQuestFocus();
    const runFit = (): void => {
      if (gen !== this.questStartFitGen) return;
      const start = compassStart(this.questViewRoute ? this.questViewBearing : 0);
      const spotPins =
        !this.questViewRoute && this.questViewSpots?.length
          ? this.questViewSpots.map((s) => ({ lng: s.lng, lat: s.lat, kind: s.kind, title: s.title }))
          : null;
      this.suppressQuestResizeRefit = true;
      this.onHomeQuestFitStart?.();
      const moved = this.camera.fitQuest(
        start.target.bearing,
        duration,
        () => {
          this.northMemory = start.memory;
        },
        spotPins,
        () => {
          this.suppressQuestResizeRefit = false;
        },
        () => this.flushDeferredQuestLayers(),
      );
      if (!moved) {
        this.suppressQuestResizeRefit = false;
        return;
      }
      this.northTarget = this.map.isEasing() ? start.target : null;
      this.paintViewControls();
    };
    runFit();
  }

  private currentView(): NorthView {
    return this.northTarget ?? { bearing: this.map.getBearing(), pitch: this.map.getPitch() };
  }

  private paintViewControls(): void {
    const bearing = this.map.getBearing();
    const view = this.currentView();
    const canvas = this.questViewBearing;
    this.myLocationControl.setShown(this.browseMode);
    this.viewControls.paint({
      bearing,
      compassShown: compassVisible(view.bearing, canvas) || compassVisible(bearing, canvas),
      compassLabel: t(COMPASS_LABEL[compassToggle(view, this.northMemory, canvas).kind]),
      homeShown: this.questFitLine() != null,
      homeLabel: t("rt.map.reset"),
    });
  }

  private goToMyLocation(): void {
    if (this.locating || !this.browseMode) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      this.onStatus(t("toast.geolocationUnavailable"));
      return;
    }
    this.locating = true;
    this.myLocationControl.setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.locating = false;
        this.myLocationControl.setBusy(false);
        const at: LngLat = [pos.coords.longitude, pos.coords.latitude];
        this.userLocation.show(at, pos.coords.accuracy);
        this.camera.locateAt(at);
        void this.camera.whenIdle().then(() => this.onMyLocationArrived?.());
      },
      (err) => {
        this.locating = false;
        this.myLocationControl.setBusy(false);
        this.onStatus(t(err.code === 1 ? "toast.geolocationDenied" : "toast.geolocationUnavailable"));
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 30_000 },
    );
  }

  /**
   * Keyboard pan, using the same pixel offset the map library used:
   * left is xDir -1, up is yDir -1, and each step is 100px.
   */
  keyboardPan(xDir: number, yDir: number): void {
    this.camera.panByKeys(xDir, yDir);
  }

  private onSurfaceLeave = (): void => {
    this.pointerOverMap = false;
    if (this.mode === "erase" || this.mode === "roadsCircle") this.clearOverlay();
    else this.refreshSelectCircle();
  };

  private onWheel = (event: WheelEvent): void => {
    if (this.inputSuspended) return;
    if (wheelTargetsMapOverlay(event)) return;
    const hit =
      typeof document !== "undefined" ? document.elementFromPoint(event.clientX, event.clientY) : event.target;
    if (!(hit instanceof Node) || !this.map.getContainer().contains(hit)) return;
    const state = {
      alt: event.altKey,
      shift: event.shiftKey,
      ctrl: event.ctrlKey,
      meta: event.metaKey,
    };
    const action = matchWheel(this.keymap, state, this.inputPlatform);
    const pixels = eraseWheelPixels(event.deltaY, event.deltaMode);
    if (this.mode === "erase" && !this.browseMode && actionMatchesWheel(this.keymap, "resizeEraseBrush", state, this.inputPlatform)) {
      event.preventDefault();
      event.stopPropagation();
      const next = resizeEraseRadius(this.eraseRadius, pixels);
      if (next === this.eraseRadius) return;
      this.eraseRadius = next;
      try {
        writeEraseRadius(localStorage, next);
      } catch {
        /* storage unavailable — the new radius still applies this session */
      }
      this.paintEraseBrush();
      this.notify(false);
      return;
    }
    if (
      this.mode === "roadsCircle" &&
      !this.browseMode &&
      actionMatchesWheel(this.keymap, "resizeRoadsBrush", state, this.inputPlatform)
    ) {
      event.preventDefault();
      event.stopPropagation();
      const next = resizeRoadsBrushRadius(this.roadsBrushRadius, pixels);
      if (next === this.roadsBrushRadius) return;
      this.roadsBrushRadius = next;
      try {
        writeRoadsBrushRadius(localStorage, next);
      } catch {
        /* the new radius still applies this session */
      }
      this.paintRoadsBrush();
      return;
    }
    if (
      !this.browseMode &&
      this.selectionTool === "circle" &&
      this.mode !== "erase" &&
      actionMatchesWheel(this.keymap, "resizeSelectCircle", state, this.inputPlatform)
    ) {
      event.preventDefault();
      event.stopPropagation();
      const next = resizeSelectCircleRadius(this.selectCircleRadius, pixels);
      if (next === this.selectCircleRadius) return;
      this.selectCircleRadius = next;
      try {
        writeSelectCircleRadius(localStorage, next);
      } catch {
        /* the new radius still applies this session */
      }
      if (this.lassoDrag?.tool === "circle" && this.cursor) this.paintSelectCircleAt(this.cursor.x, this.cursor.y);
      else this.refreshSelectCircle();
      return;
    }
    if (this.wheelZooms(action, state)) {
      if (this.browseMode) this.camera.yieldBeforeNativeWheel();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Plain wheel zooms, including while erasing. Ctrl/Cmd+wheel resizes the
   * erase brush in Erase, the select circle while that tool is active, and
   * the Fill Roads circle brush. The same chord still zooms in every other
   * mode. Shift on the zoom chord stays a slower map zoom.
   */
  private wheelZooms(action: ActionId | null, state: ModifierState): boolean {
    if (action === "zoomMap") return true;
    const brush = actionMatchesWheel(this.keymap, "resizeEraseBrush", state, this.inputPlatform);
    const circle = actionMatchesWheel(this.keymap, "resizeSelectCircle", state, this.inputPlatform);
    const roads = actionMatchesWheel(this.keymap, "resizeRoadsBrush", state, this.inputPlatform);
    if (brush && this.mode === "erase") return false;
    if (circle && this.selectionTool === "circle" && this.mode !== "erase" && this.mode !== "roadsCircle") return false;
    if (roads && this.mode === "roadsCircle") return false;
    if (brush || circle || roads) return true;
    if (state.shift && !state.alt && !state.ctrl && !state.meta) {
      const plain = matchWheel(this.keymap, { alt: false, shift: false, ctrl: false, meta: false }, this.inputPlatform);
      return plain === "zoomMap";
    }
    return false;
  }

  /** Dashed circle at the cursor. Fill Roads Circle uses a separate radius. */
  private paintRoadsBrush(): void {
    if (this.mode !== "roadsCircle") return;
    if (!this.pointerOverMap || !this.cursor) {
      this.clearOverlay();
      return;
    }
    const at = this.cursor;
    const radius = this.roadsBrushRadius;
    const preview = this.roadsCircleDrag?.preview ?? [];
    this.paintOverlay((ctx) => {
      if (preview.length > 0) {
        ctx.save();
        ctx.strokeStyle = "rgba(5, 150, 105, 0.9)";
        ctx.lineWidth = 4;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        for (const line of preview) {
          line.forEach((ll, i) => {
            const p = this.map.project(ll as [number, number]);
            if (i === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
          });
        }
        ctx.stroke();
        ctx.restore();
      }
      ctx.beginPath();
      ctx.arc(at.x, at.y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(5, 150, 105, 0.95)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.stroke();
    });
  }

  /** Dashed circle at the cursor. The radius is the same pixel size the erase hit uses. */
  private paintEraseBrush(): void {
    if (this.mode !== "erase") return;
    if (!this.pointerOverMap || !this.cursor) {
      this.clearOverlay();
      return;
    }
    const at = this.cursor;
    const radius = this.eraseRadius;
    this.paintOverlay((ctx) => {
      ctx.beginPath();
      ctx.arc(at.x, at.y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(226, 75, 61, 0.95)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.stroke();
    });
  }

  private drawLasso(ring: Array<{ x: number; y: number }>): void {
    if (ring.length < 2) {
      this.clearOverlay();
      return;
    }
    this.paintOverlay((ctx) => {
      const first = ring[0];
      if (!first) return;
      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < ring.length; i++) {
        const point = ring[i];
        if (point) ctx.lineTo(point.x, point.y);
      }
      if (ring.length >= 3) ctx.closePath();
      ctx.strokeStyle = "rgba(29, 78, 216, 0.95)";
      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.setLineDash([4, 4]);
      ctx.stroke();
    });
  }

  /**
   * Box and lasso commit on pointer-up.
   * Visible points are what the shape writes. Hidden points leave only lines, and replace clears the points.
   */
  private applyShapeSelection(drag: LassoDrag, e: PointerEvent): void {
    const mode = drag.mode;
    if (!mode) return;
    this.breakSelectAllCycle();
    const linesOnly = !this.showPoints;
    if (linesOnly) {
      const inside = this.linesInside(drag, e);
      if (mode === "replace") this.selection = [];
      this.selectedLines = applyLasso(this.selectedLines, inside, mode);
      this.noteBulk(null);
      return;
    }
    const inside = this.selectionInside(drag, e);
    const prevAnchor = this.clickOrder?.[0] ?? this.orderAnchor;
    const prev = new Set(this.selection);
    if (mode === "replace") this.selectedLines = [];
    this.selection = applyLasso(this.selection, inside, mode);
    const next = new Set(this.selection);
    const kept = prevAnchor != null && next.has(prevAnchor) ? prevAnchor : this.selection.find((index) => prev.has(index));
    this.noteBulk(kept ?? this.selection[0] ?? null);
  }

  /** Points the finished drag covers. Lasso still tests the ring; box uses the screen grid. */
  private selectionInside(drag: LassoDrag, e: PointerEvent): number[] {
    if (drag.tool === "box") {
      const origin = drag.ring[0];
      if (!origin) return [];
      const at = this.clientToLocal(e.clientX, e.clientY);
      return this.ensureScreenGrid().pointsInRect(origin.x, origin.y, at.x, at.y);
    }
    const screen = this.projectPoints();
    return screen.flatMap((point, index) => (pointInRing(point, drag.ring) ? [index] : []));
  }

  /** Lines the finished box or lasso covers, including a line that only crosses the shape. */
  private linesInside(drag: LassoDrag, e: PointerEvent): number[] {
    if (drag.tool === "box") {
      const origin = drag.ring[0];
      if (!origin) return [];
      const at = this.clientToLocal(e.clientX, e.clientY);
      return this.ensureScreenGrid().segmentsInRect(origin.x, origin.y, at.x, at.y);
    }
    return segmentsHittingRing(this.projectPoints(), this.segments, drag.ring);
  }

  private projectPoints(): Array<{ x: number; y: number }> {
    return this.points.map((point) => {
      const projected = this.map.project(point);
      return { x: projected.x, y: projected.y };
    });
  }

  /** Circle paints points and lines as the stroke moves. Box and lasso wait for pointer-up. */
  private paintCircleLive(drag: LassoDrag, at: { x: number; y: number }): void {
    if (!drag.mode) return;
    const last = drag.ring[drag.ring.length - 1] ?? at;
    let pointMark = drag.hitMark;
    if (!pointMark || pointMark.length !== this.points.length) pointMark = new Uint8Array(this.points.length);
    let lineMark = drag.lineMark;
    if (!lineMark || lineMark.length !== this.segments.length) lineMark = new Uint8Array(this.segments.length);
    drag.hitMark = pointMark;
    drag.lineMark = lineMark;
    const grid = this.ensureScreenGrid();
    const stroke = { pointMark, lineMark, replaced: drag.replaced };
    const allowPoints = this.showPoints;
    const next = applyCirclePaint(
      { points: this.selection, lines: this.selectedLines },
      allowPoints ? pointsUnderBrush(grid, last.x, last.y, at.x, at.y, this.selectCircleRadius) : [],
      segmentsUnderBrush(grid, last.x, last.y, at.x, at.y, this.selectCircleRadius, lineMark),
      drag.mode,
      stroke,
      this.segments,
      allowPoints,
    );
    drag.replaced = stroke.replaced;
    if (next) {
      this.breakSelectAllCycle();
      this.selection = next.points;
      this.selectedLines = next.lines;
      this.noteBulk(this.selection[0] ?? null);
      this.renderSelection();
      this.notify(false);
    }
    if (Math.hypot(at.x - last.x, at.y - last.y) >= 2) drag.ring.push(at);
  }

  private drawSelectBox(origin: { x: number; y: number }, at: { x: number; y: number }): void {
    const x = Math.min(origin.x, at.x);
    const y = Math.min(origin.y, at.y);
    const width = Math.abs(at.x - origin.x);
    const height = Math.abs(at.y - origin.y);
    this.paintOverlay((ctx) => {
      ctx.fillStyle = "rgba(29, 78, 216, 0.12)";
      ctx.fillRect(x, y, width, height);
      ctx.strokeStyle = "rgba(29, 78, 216, 0.95)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(x, y, width, height);
    });
  }

  /** Dashed circle in the selection color. Erase keeps the red brush. */
  private paintSelectCircleAt(x: number, y: number): void {
    const radius = this.selectCircleRadius;
    this.paintOverlay((ctx) => {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(29, 78, 216, 0.95)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.stroke();
    });
    this.selectCircleShown = true;
  }

  /**
   * Follow the cursor while Circle select is the tool. A box, lasso, or work-area
   * drag owns the overlay until it ends.
   */
  private refreshSelectCircle(): void {
    if (
      this.lassoDrag ||
      this.workDrag ||
      this.eraseDrag ||
      this.vertexDrag ||
      this.segmentDrag ||
      this.roadsBoxDrag ||
      this.roadsCircleDrag ||
      this.mode === "erase" ||
      this.mode === "roadsCircle" ||
      this.mode === "roadsBox"
    ) {
      return;
    }
    if (
      this.selectionTool === "circle" &&
      this.mode === "graph" &&
      !this.grab &&
      this.pointerOverMap &&
      this.cursor
    ) {
      this.paintSelectCircleAt(this.cursor.x, this.cursor.y);
      return;
    }
    if (this.selectCircleShown && !this.snapMarkShown) this.clearOverlay();
  }

  private drawWorkPreview(area: WorkArea): void {
    const corners = canvasCorners(area).map((ll) => this.map.project(ll));
    this.paintOverlay((ctx, width, height) => {
      ctx.fillStyle = "rgba(17, 24, 39, 0.28)";
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.moveTo(corners[0]!.x, corners[0]!.y);
      for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
      ctx.closePath();
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      ctx.beginPath();
      ctx.moveTo(corners[0]!.x, corners[0]!.y);
      for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
      ctx.closePath();
      ctx.strokeStyle = "rgba(31, 41, 55, 0.75)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([1.8, 1.8]);
      ctx.stroke();
    });
  }

  private paintOverlay(draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => void): void {
    const ctx = this.overlayCtx;
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const width = this.canvasRect.width;
    const height = this.canvasRect.height;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    draw(ctx, width, height);
  }

  private clearOverlay(): void {
    this.selectCircleShown = false;
    const ctx = this.overlayCtx;
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.overlay.width, this.overlay.height);
  }

  private pointerState(e: PointerEvent, kind: "drag" | "click"): PointerState {
    return {
      kind,
      button: e.button,
      alt: e.altKey,
      shift: e.shiftKey,
      ctrl: e.ctrlKey,
      meta: e.metaKey,
    };
  }

  private viewAction(e: PointerEvent): "panView" | "rotateView" | null {
    const action = matchPointer(this.keymap, this.pointerState(e, "drag"), this.inputPlatform);
    return action === "panView" || action === "rotateView" ? action : null;
  }

  private takePointer(e: PointerEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.claimPointer(e.pointerId);
  }

  private claimPointer(pointerId: number): void {
    try {
      this.surface.setPointerCapture(pointerId);
    } catch {
      /* the pointer can already be gone */
    }
  }

  private maybeSnap(ll: LngLat): LngLat {
    return roadSnapPoint(ll, this.roads, {
      magnetOn: this.magnetOn,
      hasWorkArea: !!this.workArea,
      radiusM: this.snapRadiusM(),
      index: this.roadIndex,
    });
  }

  /**
   * Where a dragged vertex goes. Another point or line within VERTEX_SNAP_PX
   * wins and is joined on drop; otherwise the nearest road inside the canvas.
   */
  private dragSnap(drag: VertexDrag, at: { x: number; y: number }, free: LngLat): LngLat {
    drag.snap = null;
    if (!this.magnetOn) {
      this.clearSnapMark();
      return free;
    }
    const target = this.vertexSnapTarget(at, drag.index, this.dragSnapGrid(drag));
    if (target) {
      drag.snap = target;
      this.paintSnapMark(target.x, target.y, "join");
      const point = target.kind === "vertex" ? this.points[target.index] : null;
      return point ? clone(point) : this.unprojectLocal(target);
    }
    const snapped = this.maybeSnap(free);
    if (snapped[0] !== free[0] || snapped[1] !== free[1]) {
      const p = this.map.project(snapped);
      this.paintSnapMark(p.x, p.y, "road");
    } else this.clearSnapMark();
    return snapped;
  }

  private dragSnapGrid(drag: VertexDrag): NonNullable<VertexDrag["snapGrid"]> {
    if (drag.snapGrid) return drag.snapGrid;
    const points: ScreenPoint[] = this.points.map((point) => {
      const p = this.map.project(point);
      return { x: p.x, y: p.y };
    });
    drag.snapGrid = {
      grid: buildScreenHitGrid(points, this.segments),
      points,
      hidden: incidentMask(this.segments, drag.index),
    };
    return drag.snapGrid;
  }

  private vertexSnapTarget(
    at: { x: number; y: number },
    exclude: number | null,
    grid?: NonNullable<VertexDrag["snapGrid"]>,
  ): SnapTarget | null {
    if (!this.magnetOn) return null;
    if (grid) {
      return pickSnapTarget({ grid: grid.grid, points: grid.points, segments: this.segments, at, exclude, hidden: grid.hidden });
    }
    const points: ScreenPoint[] = this.points.map((point) => {
      const p = this.map.project(point);
      return { x: p.x, y: p.y };
    });
    return pickSnapTarget({ grid: this.ensureScreenGrid(), points, segments: this.segments, at, exclude });
  }

  /** Merge a dropped vertex into the point or line it snapped to. One undo step back to before the drag. */
  private joinDrop(drag: VertexDrag, snap: SnapTarget): boolean {
    const dropped = this.points[drag.index];
    if (!dropped) return false;
    const dropAt = clone(dropped);
    this.applySnapshot(drag.before);
    const merged = joinDroppedPoint(
      this.points,
      this.segments,
      this.start,
      drag.index,
      snap.kind === "vertex" ? { kind: "vertex", index: snap.index } : { kind: "segment", segment: snap.segment, at: dropAt },
    );
    if (!merged) {
      this.points[drag.index] = dropAt;
      return false;
    }
    this.commit(() => {
      this.pendingPointMap = merged.pointMap;
      this.points = merged.points;
      this.segments = merged.segments;
      this.start = merged.start;
      this.selectOnly(merged.index);
    });
    return true;
  }

  /** Ring on the overlay: blue where the vertex will join, green on a road. */
  private paintSnapMark(x: number, y: number, kind: "join" | "road"): void {
    this.snapMarkShown = true;
    this.paintOverlay((ctx) => {
      ctx.beginPath();
      ctx.arc(x, y, kind === "join" ? 10 : 7, 0, Math.PI * 2);
      ctx.fillStyle = kind === "join" ? "rgba(29, 78, 216, 0.18)" : "rgba(5, 150, 105, 0.16)";
      ctx.fill();
      ctx.strokeStyle = kind === "join" ? "rgba(29, 78, 216, 0.95)" : "rgba(5, 150, 105, 0.95)";
      ctx.lineWidth = kind === "join" ? 2.5 : 2;
      ctx.stroke();
    });
  }

  private clearSnapMark(): void {
    if (!this.snapMarkShown) return;
    this.snapMarkShown = false;
    this.clearOverlay();
    this.refreshSelectCircle();
  }

  private outsideWorkArea(ll: LngLat): boolean {
    if (!this.workArea || pointInWorkArea(ll, this.workArea)) return false;
    this.onStatus(t("workArea.outside"));
    return true;
  }

  private wantsRoadsBox(e: PointerEvent): boolean {
    if (e.pointerType === "touch") return true;
    return this.usesButton("roadsBoxFill", e.button);
  }

  private wantsRoadsCircle(e: PointerEvent): boolean {
    if (e.pointerType === "touch") return true;
    return this.usesButton("roadsCircleStroke", e.button);
  }

  private roadsBrushRadiusM(at: LngLat): number {
    const origin = this.map.project(at);
    const edge = this.unprojectLocal({ x: origin.x + this.roadsBrushRadius, y: origin.y });
    return Math.max(1, haversineM(at, edge));
  }

  /** Toolbar mode draws with a plain left drag. Otherwise only the keymap chord does. */
  private wantsWorkRect(e: PointerEvent): boolean {
    if (
      this.mode === "work" &&
      e.button === 0 &&
      !e.altKey &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      return true;
    }
    return matchPointer(this.keymap, this.pointerState(e, "drag"), this.inputPlatform) === "drawWorkArea";
  }

  private wantsCanvasEdit(e: PointerEvent, handle: CanvasHandle): boolean {
    if (e.pointerType === "touch") return true;
    const action =
      handle === "rotate" ? "rotateCanvas" : handle === "body" ? "moveCanvas" : "resizeCanvas";
    return this.usesButton(action, e.button);
  }

  private hitCanvasHandle(x: number, y: number): CanvasHandle | null {
    if (!this.workArea || this.browseMode) return null;
    const hit2 = CANVAS_HANDLE_HIT_PX * CANVAS_HANDLE_HIT_PX;
    let best: CanvasHandle | null = null;
    let bestD = hit2;
    for (const { handle, local } of canvasHandleLocals(this.workArea)) {
      const p = this.map.project(fromLocalM(local, this.workArea));
      const d = (p.x - x) ** 2 + (p.y - y) ** 2;
      if (d <= bestD) {
        bestD = d;
        best = handle;
      }
    }
    return best;
  }

  /** True on the frame border, or anywhere inside while Canvas tool is active. */
  private hitCanvasBody(x: number, y: number): boolean {
    if (!this.workArea || this.browseMode) return false;
    const ll = this.unprojectLocal({ x, y });
    if (!pointInWorkArea(ll, this.workArea)) return false;
    if (this.mode === "work") return true;
    const { halfW, halfH } = canvasHalfSizeM(this.workArea);
    const [lx, ly] = toLocalM(ll, this.workArea);
    const edgeM = this.pxToM(12);
    return (
      Math.abs(Math.abs(lx) - halfW) <= edgeM ||
      Math.abs(Math.abs(ly) - halfH) <= edgeM
    );
  }

  private canvasFromEdit(drag: CanvasEditDrag, at: { x: number; y: number }): WorkArea | null {
    if (drag.handle === "body") {
      const ll = this.unprojectLocal(at);
      return translateCanvas(drag.start, ll[0] - drag.originLl[0], ll[1] - drag.originLl[1]);
    }
    if (drag.handle === "rotate") {
      const angle = Math.atan2(at.x - drag.centerScreen.x, drag.centerScreen.y - at.y);
      const deltaDeg = ((angle - drag.startAngle) * 180) / Math.PI;
      return rotateCanvas(drag.start, drag.startBearing + deltaDeg);
    }
    const ll = this.unprojectLocal(at);
    return resizeCanvas(drag.start, drag.handle, toLocalM(ll, drag.start));
  }

  private commitWorkArea(area: WorkArea): void {
    const before = this.snapshot();
    this.workArea = serializeWorkArea(area);
    this.pushHistory(before);
    this.storeRoads([]);
    this.extractWatch = watchForNewArea(this.workArea);
    window.clearTimeout(this.extractTimer);
    this.renderWorkArea();
    this.renderDraft();
    this.notify(true);
    this.readRoadsWhenSettled();
  }

  private snapRadiusM(): number {
    return Math.min(24, Math.max(8, this.pxToM(16)));
  }

  private pxToM(px: number): number {
    const lat = this.map.getCenter().lat;
    const mpp = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** this.map.getZoom();
    return px * mpp;
  }

  /**
   * A new canvas (load, draw, undo) is read after the camera stops and the
   * final view's tiles load: `fitWorkArea` animates, and reading at once would
   * take the far-out tiles of the previous view, which have no footpaths.
   */
  private readRoadsWhenSettled(): void {
    this.roadReadZoom = -1;
    if (!this.ready) return;
    this.map.triggerRepaint();
  }

  /** Every quiet frame with all tiles loaded. Kept cheap: it runs often. */
  private onMapIdle(): void {
    const area = this.workArea;
    if (!area || !this.ready) return;
    if (this.vertexDrag || this.segmentDrag || this.grab || this.canvasEditDrag || this.workDrag) return;
    if (this.extracting || this.blocking > 0) {
      window.setTimeout(() => this.onMapIdle(), 120);
      return;
    }
    if (this.extractWatch.settling) {
      this.extractRoads("settled");
      return;
    }
    const upgrade = shouldUpgradeRoads({
      roadCount: this.roads.length,
      readTileZoom: this.roadReadZoom,
      viewTileZoom: roadTileZoom(this.map.getZoom()),
      viewShowsCanvas: this.viewShowsCanvas(area),
    });
    if (upgrade) this.extractRoads("upgrade");
  }

  private viewShowsCanvas(area: WorkArea): boolean {
    const view = this.map.getBounds();
    const box = { west: view.getWest(), south: view.getSouth(), east: view.getEast(), north: view.getNorth() };
    return boundsOverlap(box, canvasBounds(area));
  }

  /**
   * Read roads inside the work area, ideally from ROAD_EXTRACT_ZOOM tiles.
   * OpenFreeMap transportation tiles are native zoom 14; a closer view only
   * overzooms them. The centerlines are stored in lng/lat with one spatial
   * index. Pan and rotate never call this. A read from coarser tiles is
   * topped up once the view shows the canvas with closer tiles.
   */
  private extractRoads(reason: ExtractReason): void {
    const area = this.workArea;
    if (!area || !shouldExtractRoads(this.extractWatch, area, reason)) return;
    if (!this.map.isStyleLoaded()) {
      if (reason !== "upgrade") this.extractWatch = watchAfterExtract(area, false);
      return;
    }
    if (this.extracting || this.blocking > 0) return;
    this.extracting = true;
    void this.withWaitCursor(roadExtractIsHeavy(), () => {
      try {
        const current = this.workArea;
        if (!current || !shouldExtractRoads(this.extractWatch, current, reason)) return;
        const queries = this.roadQueries();
        let lines = this.querySourceRoads(queries);
        if (lines.length === 0) lines = this.queryRenderedRoads();
        const clipped = clipLinesToWorkArea(lines, current);
        const segs: Array<[LngLat, LngLat]> = [];
        for (const line of clipped) pushRoad(segs, line);
        // Source queries only cover tiles the view loaded, so a read with the
        // canvas off screen counts as no read and the next view of it reads again.
        const sees = this.viewShowsCanvas(current);
        const tileZoom = sees ? roadTileZoom(this.map.getZoom()) : -1;
        if (reason === "upgrade") {
          this.roadReadZoom = Math.max(this.roadReadZoom, tileZoom);
          if (segs.length === 0) return;
          this.storeRoads(unionRoads(this.roads, segs));
        } else {
          this.roadReadZoom = tileZoom;
          this.storeRoads(segs);
          if (reason === "settled" && sees && segs.length === 0 && !this.browseMode) this.onStatus(t("snap.noPaths"));
        }
        const finished = this.roadSourcesLoaded();
        if (reason !== "upgrade") this.extractWatch = watchAfterExtract(current, finished);
        const wasOff = this.offRoad;
        this.renderDraft();
        if (this.offRoad !== wasOff) this.notify(false);
      } finally {
        this.extracting = false;
      }
    });
  }

  private onRoadSourceData(event: { sourceId?: string }): void {
    if (!shouldExtractRoads(this.extractWatch, this.workArea, "tiles")) return;
    const sourceId = event.sourceId;
    if (this.roadSourceIds.size > 0 && sourceId && !this.roadSourceIds.has(sourceId)) return;
    window.clearTimeout(this.extractTimer);
    this.extractTimer = window.setTimeout(() => {
      if (!shouldExtractRoads(this.extractWatch, this.workArea, "tiles")) return;
      this.extractRoads("tiles");
    }, 200);
  }

  private roadQueries(): Array<{ source: string; sourceLayer: string }> {
    const layers = this.map.getStyle().layers ?? [];
    const seen = new Set<string>();
    const out: Array<{ source: string; sourceLayer: string }> = [];
    const sources = new Set<string>();
    for (const layer of layers) {
      if (!isRoadLayer(layer.id) || !this.map.getLayer(layer.id)) continue;
      const spec = layer as { source?: string; "source-layer"?: string };
      const source = spec.source;
      const sourceLayer = spec["source-layer"];
      if (!source || !sourceLayer) continue;
      sources.add(source);
      const key = `${source}\0${sourceLayer}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ source, sourceLayer });
    }
    if (sources.size > 0) this.roadSourceIds = sources;
    return out;
  }

  private querySourceRoads(queries: Array<{ source: string; sourceLayer: string }>): LngLat[][] {
    const lines: LngLat[][] = [];
    for (const query of queries) {
      try {
        collectRoadLines(this.map.querySourceFeatures(query.source, { sourceLayer: query.sourceLayer }), lines);
      } catch {
        /* this source is not loaded yet; a later sourcedata event retries once */
      }
    }
    return lines;
  }

  private queryRenderedRoads(): LngLat[][] {
    const layers = (this.map.getStyle().layers ?? [])
      .map((layer) => layer.id)
      .filter((id) => isRoadLayer(id) && this.map.getLayer(id));
    if (layers.length === 0) return [];
    let features: MapGeoJSONFeature[] = [];
    try {
      features = this.map.queryRenderedFeatures({ layers });
    } catch {
      return [];
    }
    const lines: LngLat[][] = [];
    collectRoadLines(features, lines);
    return lines;
  }

  private roadSourcesLoaded(): boolean {
    if (this.roadSourceIds.size === 0) return false;
    for (const id of this.roadSourceIds) {
      if (!this.map.isSourceLoaded(id)) return false;
    }
    return true;
  }

  /** One spatial index per stored road set. Segment colors reuse it. */
  private storeRoads(roads: Array<[LngLat, LngLat]>): void {
    this.roads = roads;
    this.roadIndex = roads.length > 0 ? indexRoads(roads) : null;
    this.scene.replaceRoads(this.roads, this.roadIndex);
  }

  private draftInput(): DraftInput {
    return {
      points: this.points,
      segments: this.segments,
      selection: this.browseMode ? [] : this.selection,
      selectedLines: this.browseMode ? [] : this.selectedLines,
      showPoints: this.showPoints && !this.browseMode,
      start: this.start,
      selectAll: this.highlightMode(),
      pointStyles: this.pointStyles,
      lineStyles: this.lineStyles,
      decorative: this.decorative,
      decorativeStroke: this.decorativeStroke,
    };
  }

  /** Selection tint only. The screen grid stays, so a circle stroke does not reproject. */
  private renderSelection(): void {
    if (!this.ready) return;
    this.ensureLayers();
    const change = this.scene.apply(this.draftInput());
    this.offRoad = change.offRoad;
    if (change.kind === "none") return;
    const src = this.map.getSource(DRAFT_SOURCE) as maplibregl.GeoJSONSource;
    if (change.kind === "set") {
      src.setData({ type: "FeatureCollection", features: change.features ?? [] });
      return;
    }
    if (change.diff) src.updateData(change.diff);
  }

  private renderDraft(): void {
    const pointMap = this.pendingPointMap;
    this.pendingPointMap = null;
    this.scheduleRegionSync();
    if (!this.ready) return;
    this.invalidateScreenGrid();
    this.ensureLayers();
    this.renderRegions();
    const change = this.scene.apply({ ...this.draftInput(), pointMap });
    this.offRoad = change.offRoad;
    if (change.kind === "none") {
        if (this.questViewSpots?.length) this.syncHomeQuestSpots();
      return;
    }
    const src = this.map.getSource(DRAFT_SOURCE) as maplibregl.GeoJSONSource;
    if (change.kind === "set") {
      src.setData({ type: "FeatureCollection", features: change.features ?? [] });
        if (this.questViewSpots?.length) this.syncHomeQuestSpots();
      return;
    }
    if (change.diff) src.updateData(change.diff);
        if (this.questViewSpots?.length) this.syncHomeQuestSpots();
  }

  /**
   * Faces are cached on the segment list. A point move keeps the same cycles,
   * so the polygons follow the points without a rebuild. A topology edit
   * rebuilds once, then drops a fill whose cycle opened, or keeps the color
   * on the piece that still contains the original click.
   */
  /**
   * A line edit rebuilds faces when the browser is idle. Point moves keep the
   * cached cycles, so the polygons follow the points on the same frame.
   * A fill click builds immediately when the cache is stale.
   */
  private scheduleRegionSync(): void {
    const sig = segmentSignature(this.segments);
    if (this.regionKnown && sig === this.regionSig) return;
    if (this.regionIdle != null) return;
    const run = () => {
      this.regionIdle = null;
      const before = this.regions;
      this.syncRegionsNow();
      this.renderRegions();
      if (!sameRegions(before, this.regions)) this.notify(true);
    };
    if (typeof requestIdleCallback === "function") {
      this.regionIdle = requestIdleCallback(run, { timeout: 80 });
    } else {
      this.regionIdle = window.setTimeout(run, 16);
    }
  }

  /** Cached faces when the lines have not changed. A fresh build otherwise. */
  private facesForHit(): PlanarFace[] {
    const sig = segmentSignature(this.segments);
    if (!this.regionKnown || sig !== this.regionSig) {
      this.syncRegionsNow();
    } else {
      refreshFaceBounds(this.regionFaces, this.points);
    }
    return this.regionFaces;
  }

  private syncRegionsNow(): void {
    if (this.regionIdle != null) {
      if (typeof cancelIdleCallback === "function") cancelIdleCallback(this.regionIdle);
      else window.clearTimeout(this.regionIdle);
      this.regionIdle = null;
    }
    const sig = segmentSignature(this.segments);
    if (this.regionKnown && sig === this.regionSig) return;
    const faces = planarFaces(this.points, this.segments);
    this.regionFaces = faces;
    this.regionSig = sig;
    this.regionKnown = true;
    const next = reconcileFills(this.regions, faces, this.points);
    if (!sameRegions(this.regions, next)) {
      this.regions = next;
      this.regionLayer.invalidate();
    }
  }

  private renderRegions(): void {
    if (!this.ready) return;
    this.ensureLayers();
    const data = this.regionLayer.update(this.regions, this.points);
    if (!data) return;
    const src = this.map.getSource(REGION_SOURCE) as maplibregl.GeoJSONSource | undefined;
    src?.setData(data);
  }

  private renderBoundary(): void {
    if (!this.ready) return;
    this.ensureLayers();
    const ring = this.boundary.map(clone);
    const features: Array<Record<string, unknown>> = [];
    if (ring.length >= 2) {
      const stroke = this.boundaryClosed ? [...ring, ring[0]] : ring;
      features.push({
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: stroke },
      });
    }
    if (ring.length >= 3) {
      features.push({
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [[...ring, ring[0]]] },
      });
    }
    for (let i = 0; i < ring.length; i++) {
      const origin = i === 0 && !this.boundaryClosed;
      features.push({
        type: "Feature",
        properties: { role: origin ? "origin" : "vertex" },
        geometry: { type: "Point", coordinates: ring[i] },
      });
    }
    const src = this.map.getSource(BOUNDARY_SOURCE) as maplibregl.GeoJSONSource;
    src.setData({ type: "FeatureCollection", features });
  }

  private renderWorkArea(): void {
    if (!this.ready) return;
    this.ensureLayers();
    const dragging = !!(this.workDrag?.moved || this.canvasEditDrag?.moved);
    const area = this.browseMode || dragging ? null : (this.workPreview ?? this.workArea);
    const showHandles = !!area && !this.browseMode && !dragging;
    const src = this.map.getSource(WORK_SOURCE) as maplibregl.GeoJSONSource;
    src.setData({
      type: "FeatureCollection",
      features: area ? workAreaFeatures(area, showHandles) : [],
    });
  }

  private fitWorkArea(): void {
    if (!this.workArea) return;
    const area = this.workArea;
    this.camera.fitCanvas(canvasBounds(area), canvasBearing(area));
  }

  private ensureLayers(): void {
    if (!mapHasStyleObject(this.map)) return;
    if (!this.map.getSource(DRAFT_SOURCE)) {
      this.map.addSource(DRAFT_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      const lineColor = DRAFT_LINE_COLOR as maplibregl.ExpressionSpecification;
      const lineWidth = DRAFT_LINE_WIDTH as maplibregl.ExpressionSpecification;
      const solid = SOLID_LINE_FILTER as maplibregl.FilterSpecification;
      const dashed = DASHED_LINE_FILTER as maplibregl.FilterSpecification;
      const dotted = DOTTED_LINE_FILTER as maplibregl.FilterSpecification;
      const decorative = DECORATIVE_LINE_FILTER as maplibregl.FilterSpecification;
      this.map.addLayer({
        id: DRAFT_LINE,
        type: "line",
        source: DRAFT_SOURCE,
        filter: solid,
        layout: ROUTE_LINE_LAYOUT,
        paint: { "line-color": lineColor, "line-width": lineWidth, "line-opacity": DRAFT_LINE_OPACITY },
      });
      this.map.addLayer({
        id: DRAFT_LINE_DASH,
        type: "line",
        source: DRAFT_SOURCE,
        filter: dashed,
        layout: ROUTE_LINE_LAYOUT,
        paint: {
          "line-color": lineColor,
          "line-width": lineWidth,
          "line-opacity": DRAFT_LINE_OPACITY,
          "line-dasharray": [...DASHED_PATTERN],
        },
      });
      this.map.addLayer({
        id: DRAFT_LINE_DOT,
        type: "line",
        source: DRAFT_SOURCE,
        filter: dotted,
        layout: ROUTE_LINE_LAYOUT,
        paint: {
          "line-color": lineColor,
          "line-width": lineWidth,
          "line-opacity": DRAFT_LINE_OPACITY,
          "line-dasharray": [...DOTTED_PATTERN],
        },
      });
      this.map.addLayer({
        id: DRAFT_LINE_DECOR_HIT,
        type: "line",
        source: DRAFT_SOURCE,
        filter: EDITOR_DECORATIVE_HIT_FILTER as maplibregl.FilterSpecification,
        layout: ROUTE_LINE_LAYOUT,
        paint: {
          "line-color": lineColor,
          "line-width": EDITOR_DECORATIVE_HIT_WIDTH as maplibregl.ExpressionSpecification,
          "line-opacity": 0,
        },
      });
      this.map.addLayer({
        id: DRAFT_LINE_DECOR,
        type: "line",
        source: DRAFT_SOURCE,
        filter: decorative,
        layout: ROUTE_LINE_LAYOUT,
        paint: {
          "line-color": lineColor,
          "line-width": lineWidth,
          "line-opacity": EDITOR_DECORATIVE_LINE_OPACITY as maplibregl.ExpressionSpecification,
          "line-dasharray": [2, 2.5],
        },
      });
      this.map.addLayer({
        id: DRAFT_PICK,
        type: "line",
        source: DRAFT_SOURCE,
        filter: ["==", ["coalesce", ["get", "picked"], 0], 1],
        layout: ROUTE_LINE_LAYOUT,
        paint: {
          "line-color": "#1d4ed8",
          "line-width": ["+", ["coalesce", ["get", "width"], 4.5], 1.5],
          "line-opacity": 0.95,
        },
      });
      if (!this.map.getSource(REGION_SOURCE)) {
        this.map.addSource(REGION_SOURCE, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        // Under the colored stroke. Antialiasing off keeps the polygon on the
        // vertex ring, which is the centerline.
        this.map.addLayer(
          {
            id: REGION_FILL,
            type: "fill",
            source: REGION_SOURCE,
            paint: {
              "fill-color": ["coalesce", ["get", "color"], "#3b82f6"],
              "fill-opacity": 0.42,
              "fill-antialias": REGION_FILL_ANTIALIAS,
            },
          },
          DRAFT_LINE,
        );
      }
      const shadowPaint = ROUTE_LINE_SHADOW_PAINT as maplibregl.LineLayerSpecification["paint"];
      const addRouteShadow = (id: string, filter: maplibregl.FilterSpecification) => {
        this.map.addLayer(
          {
            id,
            type: "line",
            source: DRAFT_SOURCE,
            filter,
            layout: ROUTE_LINE_LAYOUT,
            paint: shadowPaint,
          },
          DRAFT_LINE,
        );
      };
      if (!this.map.getLayer(DRAFT_LINE_SHADOW)) {
        addRouteShadow(DRAFT_LINE_SHADOW, solid);
        addRouteShadow(DRAFT_LINE_SHADOW_DASH, dashed);
        addRouteShadow(DRAFT_LINE_SHADOW_DOT, dotted);
      }
      this.map.addLayer({
        id: DRAFT_POINTS,
        type: "circle",
        source: DRAFT_SOURCE,
        filter: ["==", "$type", "Point"],
        paint: {
          "circle-radius": ["coalesce", ["get", "radius"], ["match", ["get", "role"], "selected", 8, "start", 7, 5.5]],
          "circle-color": [
            "coalesce",
            ["get", "color"],
            ["match", ["get", "role"], "selected", "#1d4ed8", "start", "#111827", "#ffffff"],
          ],
          "circle-stroke-color": ["case", ["all", ["==", ["get", "role"], "selected"], ["has", "color"]], "#1d4ed8", "#ffffff"],
          "circle-stroke-width": ["case", ["all", ["==", ["get", "role"], "selected"], ["has", "color"]], 3, 2],
        },
      });
    }
    if (!this.map.getSource(ISSUE_SOURCE)) {
      this.map.addSource(ISSUE_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      // A halo under doubled lines, orange over cut-off lines, and rings under
      // the vertices. Hit tests use the screen grid, so these never take clicks.
      this.map.addLayer(
        {
          id: ISSUE_OVERLAP,
          type: "line",
          source: ISSUE_SOURCE,
          filter: ["==", ["get", "kind"], "overlap"],
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": "#d946ef", "line-width": 14, "line-opacity": 0.45 },
        },
        DRAFT_LINE,
      );
      this.map.addLayer(
        {
          id: ISSUE_STRAY,
          type: "line",
          source: ISSUE_SOURCE,
          filter: ["==", ["get", "kind"], "stray"],
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": "#f97316", "line-width": 5, "line-opacity": 0.95 },
        },
        DRAFT_PICK,
      );
      this.map.addLayer(
        {
          id: ISSUE_GAP_LINK,
          type: "line",
          source: ISSUE_SOURCE,
          filter: ["==", ["get", "kind"], "gap-link"],
          paint: { "line-color": "#dc2626", "line-width": 2, "line-dasharray": [1.5, 1.5] },
        },
        DRAFT_POINTS,
      );
      this.map.addLayer(
        {
          id: ISSUE_MARKS,
          type: "circle",
          source: ISSUE_SOURCE,
          filter: ["==", "$type", "Point"],
          paint: {
            "circle-radius": ["match", ["get", "kind"], "lone", 12, "gap", 10, 9],
            "circle-color": ["match", ["get", "kind"], "gap", "#fbbf24", "#ffffff"],
            "circle-opacity": ["match", ["get", "kind"], "gap", 0.5, "crossing", 0.6, 0],
            "circle-stroke-color": ["match", ["get", "kind"], "lone", "#dc2626", "gap", "#ea580c", "#c026d3"],
            "circle-stroke-width": 3,
          },
        },
        DRAFT_POINTS,
      );
      if (this.browseMode) {
        for (const id of [ISSUE_OVERLAP, ISSUE_STRAY, ISSUE_GAP_LINK, ISSUE_MARKS]) {
          this.map.setLayoutProperty(id, "visibility", "none");
        }
      }
    }
    ensureNearbyLayers(this.map);
    if (!this.map.getSource(WORK_SOURCE)) {
      this.map.addSource(WORK_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      const before = this.map.getLayer(DRAFT_LINE) ? DRAFT_LINE : undefined;
      this.map.addLayer(
        {
          id: WORK_DIM,
          type: "fill",
          source: WORK_SOURCE,
          filter: ["==", "$type", "Polygon"],
          paint: {
            "fill-color": "#111827",
            "fill-opacity": 0.28,
          },
        },
        before,
      );
      this.map.addLayer(
        {
          id: WORK_LINE,
          type: "line",
          source: WORK_SOURCE,
          filter: ["all", ["==", "$type", "LineString"], ["!has", "role"]],
          paint: {
            "line-color": "#1f2937",
            "line-width": 1.5,
            "line-opacity": 0.75,
            "line-dasharray": [1.2, 1.2],
          },
        },
        before,
      );
      this.map.addLayer(
        {
          id: WORK_ROTATE_ARM,
          type: "line",
          source: WORK_SOURCE,
          filter: ["==", ["get", "role"], "rotate-arm"],
          paint: {
            "line-color": "#1f2937",
            "line-width": 1.25,
            "line-opacity": 0.7,
          },
        },
        before,
      );
      this.map.addLayer(
        {
          id: WORK_HANDLES,
          type: "circle",
          source: WORK_SOURCE,
          filter: ["==", "$type", "Point"],
          paint: {
            "circle-radius": [
              "case",
              ["==", ["get", "role"], "rotate"],
              6,
              5,
            ],
            "circle-color": [
              "case",
              ["==", ["get", "role"], "rotate"],
              "#f8fafc",
              "#ffffff",
            ],
            "circle-stroke-color": "#1f2937",
            "circle-stroke-width": 1.5,
          },
        },
        before,
      );
    }
    if (!this.map.getSource(BOUNDARY_SOURCE)) {
      this.map.addSource(BOUNDARY_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      const before = this.map.getLayer(DRAFT_LINE) ? DRAFT_LINE : undefined;
      this.map.addLayer(
        {
          id: BOUNDARY_FILL,
          type: "fill",
          source: BOUNDARY_SOURCE,
          filter: ["==", "$type", "Polygon"],
          paint: {
            "fill-color": "#3a86ff",
            "fill-opacity": 0.18,
          },
        },
        before,
      );
      this.map.addLayer(
        {
          id: BOUNDARY_LINE,
          type: "line",
          source: BOUNDARY_SOURCE,
          filter: ["==", "$type", "LineString"],
          paint: {
            "line-color": "#3a86ff",
            "line-width": 2.5,
            "line-opacity": 0.95,
          },
        },
        before,
      );
      this.map.addLayer(
        {
          id: BOUNDARY_POINTS,
          type: "circle",
          source: BOUNDARY_SOURCE,
          filter: ["==", "$type", "Point"],
          paint: {
            "circle-radius": ["match", ["get", "role"], "origin", 8, 5.5],
            "circle-color": ["match", ["get", "role"], "origin", "#1d4ed8", "#ffffff"],
            "circle-stroke-color": "#3a86ff",
            "circle-stroke-width": 2,
          },
        },
        before,
      );
    }
    ensureCachedRouteLines(this.map, DRAFT_LINE);
    this.basemap.reconcileLayerStack();
  }

  private hitVertex(x: number, y: number): number | null {
    return this.ensureScreenGrid().nearestPoint(x, y, 18);
  }

  private hitSegment(x: number, y: number, radius: number): number | null {
    return this.ensureScreenGrid().nearestSegment(x, y, radius, this.eraseDrag?.stroke.hidden);
  }

  private invalidateScreenGrid(): void {
    this.screenGrid = null;
  }

  private ensureScreenGrid(): ScreenHitGrid {
    if (this.screenGrid) return this.screenGrid;
    const projected: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < this.points.length; i++) {
      const point = this.points[i];
      const screen = point ? this.map.project(point) : { x: Number.NaN, y: Number.NaN };
      projected.push({ x: screen.x, y: screen.y });
    }
    this.screenGrid = buildScreenHitGrid(projected, this.segments);
    return this.screenGrid;
  }

  private segmentHitPx(kind: string): number {
    return kind === "touch" ? SEGMENT_HIT_TOUCH_PX : SEGMENT_HIT_FINE_PX;
  }

  private hitBoundaryVertex(x: number, y: number, radius: number): number | null {
    let best: number | null = null;
    let bestD = Infinity;
    for (let i = 0; i < this.boundary.length; i++) {
      const limit = i === 0 && !this.boundaryClosed ? Math.max(radius, BOUNDARY_CLOSE_PX) : radius;
      const p = this.map.project(this.boundary[i]);
      const d = Math.hypot(p.x - x, p.y - y);
      if (d <= limit && d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  private onCursorKey = (e: KeyboardEvent): void => {
    if (!this.syncMods(e)) return;
    this.refreshCursor();
  };

  private onCursorBlur = (): void => {
    this.mods = { alt: false, shift: false, ctrl: false, meta: false };
    this.refreshCursor();
  };

  private syncMods(e: { altKey: boolean; shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }): boolean {
    if (
      this.mods.alt === e.altKey &&
      this.mods.shift === e.shiftKey &&
      this.mods.ctrl === e.ctrlKey &&
      this.mods.meta === e.metaKey
    ) {
      return false;
    }
    this.mods = { alt: e.altKey, shift: e.shiftKey, ctrl: e.ctrlKey, meta: e.metaKey };
    return true;
  }

  /** Point, Line, nearby piece, or an Erase target. Boundary and Work Area are not features. */
  private featureUnderCursor(): boolean {
    if (this.overNearby) return true;
    if (!this.ready || !this.cursor) return false;
    const { x, y } = this.cursor;
    if (this.mode === "erase") return this.hitSegment(x, y, this.eraseRadius) != null;
    if (this.mode !== "graph") return false;
    if (this.showPoints && this.hitVertex(x, y) != null) return true;
    return this.hitSegment(x, y, SEGMENT_HIT_FINE_PX) != null;
  }

  private applyBrowseLayers(): void {
    if (!this.ready || !mapHasStyleObject(this.map)) return;
    try {
      const vis = this.browseMode ? "none" : "visible";
      for (const id of [
        DRAFT_POINTS,
        DRAFT_PICK,
        ISSUE_OVERLAP,
        ISSUE_STRAY,
        ISSUE_GAP_LINK,
        ISSUE_MARKS,
        WORK_DIM,
        WORK_LINE,
        WORK_ROTATE_ARM,
        WORK_HANDLES,
        BOUNDARY_FILL,
        BOUNDARY_LINE,
        BOUNDARY_POINTS,
      ]) {
        if (mapHasLayer(this.map, id)) this.map.setLayoutProperty(id, "visibility", vis);
      }
      const draftLineVis = this.browseMode && this.questRouteOnMap ? "none" : "visible";
      for (const id of [
        DRAFT_LINE,
        DRAFT_LINE_SHADOW,
        DRAFT_LINE_SHADOW_DASH,
        DRAFT_LINE_SHADOW_DOT,
        DRAFT_LINE_DASH,
        DRAFT_LINE_DOT,
        DRAFT_LINE_DECOR_HIT,
        DRAFT_LINE_DECOR,
      ]) {
        if (mapHasLayer(this.map, id)) this.map.setLayoutProperty(id, "visibility", draftLineVis);
      }
      this.basemap.reconcileLayerStack();
    } catch {
      /* style swap while toggling browse layers */
    }
  }

  private refreshCursor(): void {
    if (this.browseMode && this.blocking === 0) {
      this.surface.classList.remove("cursor-wait");
      this.surface.classList.remove("lasso-drag");
      this.surface.classList.remove("grab-move");
      let view: ViewCursor = null;
      let panning = false;
      if (this.viewDrag?.action === "panView") {
        view = "pan";
        panning = true;
      } else if (this.viewDrag?.action === "rotateView") {
        view = "rotate";
      } else {
        const held = heldView(this.keymap, this.mods, this.inputPlatform);
        view = held === "panView" ? "pan" : held === "rotateView" ? "rotate" : null;
      }
      this.setCanvasCursor(canvasCursor({ view, panning, overFeature: false, lasso: false, moving: false }));
      return;
    }
    if (this.blocking > 0) {
      this.surface.classList.add("cursor-wait");
      this.surface.classList.remove("lasso-drag");
      this.surface.classList.remove("grab-move");
      this.setCanvasCursor("wait");
      return;
    }
    let view: ViewCursor = null;
    let panning = false;
    if (this.viewDrag?.action === "panView") {
      view = "pan";
      panning = true;
    } else if (this.viewDrag?.action === "rotateView") {
      view = "rotate";
    } else {
      const held = heldView(this.keymap, this.mods, this.inputPlatform);
      view = held === "panView" ? "pan" : held === "rotateView" ? "rotate" : null;
    }
    const moving = this.grab != null;
    const lasso = !moving && this.lassoDrag?.moved === true && this.lassoDrag.mode != null;
    const filling = this.mode === "fill" && view == null && !moving;
    const overFeature = !moving && view == null && !lasso && !filling && this.featureUnderCursor();
    this.setCanvasCursor(filling ? fillCursor(this.regionColor) : canvasCursor({ view, panning, overFeature, lasso, moving }));
    this.surface.classList.toggle("lasso-drag", lasso && view == null && !panning);
    this.surface.classList.toggle("grab-move", moving);
  }

  private setCanvasCursor(cursor: string): void {
    if (this.appliedCursor === cursor) return;
    this.appliedCursor = cursor;
    this.map.getCanvas().style.cursor = cursor;
  }

  /**
   * Known-heavy work yields once so the hourglass actually paints, then runs.
   * Short work keeps the current cursor and does not wait a frame.
   */
  private async withWaitCursor<T>(heavy: boolean, work: () => T): Promise<T> {
    if (!heavy) return work();
    this.blocking += 1;
    this.surface.classList.add("cursor-wait");
    this.surface.classList.remove("lasso-drag");
    this.surface.classList.remove("grab-move");
    this.setCanvasCursor("wait");
    try {
      await yieldForPaint();
      return work();
    } finally {
      this.blocking -= 1;
      if (this.blocking === 0) {
        this.surface.classList.remove("cursor-wait");
        this.appliedCursor = null;
        this.refreshCursor();
      }
    }
  }

  private pushDraftDiff(diff: DraftDiff | null): void {
    if (!diff || !this.ready) return;
    const src = this.map.getSource(DRAFT_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    src.updateData(diff);
  }

  private refreshCanvasRect = (): void => {
    const rect = this.map.getCanvas().getBoundingClientRect();
    this.canvasRect = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const bufferWidth = Math.round(width * dpr);
    const bufferHeight = Math.round(height * dpr);
    if (this.overlay.width !== bufferWidth || this.overlay.height !== bufferHeight) {
      this.overlay.width = bufferWidth;
      this.overlay.height = bufferHeight;
    }
    this.overlay.style.width = `${width}px`;
    this.overlay.style.height = `${height}px`;
    if (this.skipNextQuestResizeRefit) {
      this.skipNextQuestResizeRefit = false;
      this.camera.ackCanvasSize(width, height);
    } else if (this.suppressQuestResizeRefit) {
      this.camera.ackCanvasSize(width, height);
    } else if (
      this.camera.canvasResized(width, height) &&
      !this.viewDrag &&
      !this.camera.programmaticMotionActive()
    ) {
      this.showQuestStart(0);
    }
  };

  private clientToLocal(clientX: number, clientY: number): { x: number; y: number } {
    return { x: clientX - this.canvasRect.left, y: clientY - this.canvasRect.top };
  }

  private unprojectLocal(p: { x: number; y: number }): LngLat {
    const ll = this.map.unproject([p.x, p.y]);
    return [ll.lng, ll.lat];
  }

  private notify(edited: boolean): void {
    this.scheduleIssues();
    this.onChange(edited);
  }

  private forgetSelection(): void {
    this.selection = [];
    this.selectedLines = [];
    this.clickOrder = null;
    this.orderAnchor = null;
    this.breakSelectAllCycle();
  }

  private breakSelectAllCycle(): void {
    this.selectionRevision += 1;
  }

  private selectOnly(index: number): void {
    this.breakSelectAllCycle();
    this.selection = [index];
    this.selectedLines = [];
    this.clickOrder = [index];
    this.orderAnchor = index;
  }

  /** Lasso, box, circle, select-all, and index remaps do not know click order. */
  private noteBulk(anchor: number | null): void {
    this.clickOrder = null;
    this.orderAnchor = anchor;
  }

  private noteClick(prev: readonly number[], next: readonly number[]): void {
    if (next.length === 0) {
      this.clickOrder = null;
      this.orderAnchor = null;
      return;
    }
    if (next.length === 1) {
      const only = next[0];
      if (only == null) return;
      this.clickOrder = [only];
      this.orderAnchor = only;
      return;
    }
    const nextSet = new Set(next);
    const prevSet = new Set(prev);
    const added = next.filter((index) => !prevSet.has(index));
    if (this.clickOrder) {
      const kept = this.clickOrder.filter((index) => nextSet.has(index));
      if (added.length === 1) {
        const extra = added[0];
        if (extra != null) kept.push(extra);
      }
      if (added.length <= 1 && kept.length === next.length) {
        this.clickOrder = kept;
        this.orderAnchor = kept[0] ?? null;
        return;
      }
    }
    if (this.orderAnchor != null && nextSet.has(this.orderAnchor)) {
      this.clickOrder = null;
      return;
    }
    this.clickOrder = null;
    this.orderAnchor = next[0] ?? null;
  }

  private pointSelection(): number[] {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const index of this.selection) {
      if (index < 0 || index >= this.points.length || seen.has(index)) continue;
      seen.add(index);
      out.push(index);
    }
    return out;
  }

  private canJoinOrMerge(): boolean {
    return this.mode === "graph" && this.blocking === 0 && this.grab == null && !this.deleteMenuOpen;
  }

  private assignClick(hit: Pick, shift: boolean): void {
    const next = applyClick({ points: this.selection, lines: this.selectedLines }, hit, shift);
    if (sameIds(this.selection, next.points) && sameIds(this.selectedLines, next.lines)) return;
    const prev = this.selection;
    this.breakSelectAllCycle();
    this.selection = next.points;
    this.selectedLines = next.lines;
    if (hit.kind === "point" || hit.kind === "empty" || next.points.length === 0) this.noteClick(prev, next.points);
    this.renderDraft();
    this.notify(false);
  }
}

function pointerKind(event: Event): string {
  if (typeof TouchEvent !== "undefined" && event instanceof TouchEvent) return "touch";
  if ("pointerType" in event && typeof (event as PointerEvent).pointerType === "string") {
    return (event as PointerEvent).pointerType || "mouse";
  }
  return "mouse";
}

function clone(p: LngLat): LngLat {
  return [p[0], p[1]];
}

function sameIds(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function sameWorkArea(a: WorkArea | null, b: WorkArea | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.west === b.west && a.south === b.south && a.east === b.east && a.north === b.north && (a.bearing ?? 0) === (b.bearing ?? 0)
  );
}

function sameSnapshot(a: Snapshot, b: Snapshot): boolean {
  if (a.start !== b.start || a.spacing !== b.spacing) return false;
  if (!sameIds(a.selection, b.selection) || !sameIds(a.selectedLines, b.selectedLines)) return false;
  if (a.points.length !== b.points.length || a.segments.length !== b.segments.length) return false;
  for (let i = 0; i < a.points.length; i++) {
    if (a.points[i][0] !== b.points[i][0] || a.points[i][1] !== b.points[i][1]) return false;
  }
  for (let i = 0; i < a.segments.length; i++) {
    if (a.segments[i][0] !== b.segments[i][0] || a.segments[i][1] !== b.segments[i][1]) return false;
  }
  if (!samePointStyleList(a.pointStyles, b.pointStyles) || !sameLineStyleList(a.lineStyles, b.lineStyles)) return false;
  if (a.decorative.length !== b.decorative.length) return false;
  for (let i = 0; i < a.decorative.length; i++) {
    if (a.decorative[i] !== b.decorative[i]) return false;
  }
  if (a.pieceStyleLocked !== b.pieceStyleLocked) return false;
  if (!sameWorkArea(a.workArea, b.workArea)) return false;
  if (!samePointStyle(a.styleDefaults.point, b.styleDefaults.point) || !sameLineStyle(a.styleDefaults.line, b.styleDefaults.line)) {
    return false;
  }
  return sameFill(a.fill, b.fill) && sameRegions(a.regions, b.regions);
}

function sameFill(a: FillMemory | null, b: FillMemory | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (!samePoints(a.basePoints, b.basePoints)) return false;
  if (!sameGraph(a.welded, b.welded) || !sameGraph(a.resampled, b.resampled)) return false;
  if (a.covers.length !== b.covers.length) return false;
  for (let i = 0; i < a.covers.length; i++) {
    if (a.covers[i].length !== b.covers[i].length) return false;
    for (let j = 0; j < a.covers[i].length; j++) {
      if (a.covers[i][j] !== b.covers[i][j]) return false;
    }
  }
  return true;
}

function sameGraph(a: PieceGraph, b: PieceGraph): boolean {
  if (a.start !== b.start) return false;
  if (!samePoints(a.points, b.points)) return false;
  if (a.segments.length !== b.segments.length) return false;
  for (let i = 0; i < a.segments.length; i++) {
    if (a.segments[i][0] !== b.segments[i][0] || a.segments[i][1] !== b.segments[i][1]) return false;
  }
  return true;
}

function samePoints(a: LngLat[], b: LngLat[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i][0] !== b[i][0] || a[i][1] !== b[i][1]) return false;
  }
  return true;
}

function cloneGraph(graph: PieceGraph): PieceGraph {
  return {
    points: graph.points.map(clone),
    segments: graph.segments.map(([a, b]) => [a, b]),
    start: graph.start,
  };
}

function cloneFill(fill: FillMemory | null): FillMemory | null {
  if (!fill) return null;
  return {
    basePoints: fill.basePoints.map(clone),
    welded: cloneGraph(fill.welded),
    resampled: cloneGraph(fill.resampled),
    covers: fill.covers.map((cover) => cover.slice()),
  };
}

function collectRoadLines(
  features: ReadonlyArray<{
    properties?: { class?: unknown } | null;
    geometry: { type: string; coordinates: unknown };
  }>,
  lines: LngLat[][],
): void {
  for (const feature of features) {
    const cls = String(feature.properties?.class ?? "");
    if (cls === "rail" || cls === "transit" || cls === "ferry" || cls === "aerialway") continue;
    const geom = feature.geometry;
    if (geom.type === "LineString") pushLine(lines, geom.coordinates as number[][]);
    else if (geom.type === "MultiLineString") {
      const parts = geom.coordinates as number[][][];
      for (const line of parts) pushLine(lines, line);
    }
  }
}

function workAreaFeatures(
  area: WorkArea,
  showHandles: boolean,
): Array<{
  type: "Feature";
  properties: Record<string, unknown>;
  geometry:
    | { type: "Polygon"; coordinates: [number, number][][] }
    | { type: "LineString"; coordinates: [number, number][] }
    | { type: "Point"; coordinates: [number, number] };
}> {
  const world: [number, number][] = [
    [-180, -85],
    [180, -85],
    [180, 85],
    [-180, 85],
    [-180, -85],
  ];
  const corners = canvasCorners(area);
  const hole: [number, number][] = [...corners, corners[0]!];
  const outline: [number, number][] = [...corners, corners[0]!];
  const features: Array<{
    type: "Feature";
    properties: Record<string, unknown>;
    geometry:
      | { type: "Polygon"; coordinates: [number, number][][] }
      | { type: "LineString"; coordinates: [number, number][] }
      | { type: "Point"; coordinates: [number, number] };
  }> = [
    {
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [world, hole] },
    },
    {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: outline },
    },
  ];
  if (showHandles) {
    const handles = canvasHandleLocals(area);
    const top = handles.find((h) => h.handle === "n");
    const rotate = handles.find((h) => h.handle === "rotate");
    if (top && rotate) {
      features.push({
        type: "Feature",
        properties: { role: "rotate-arm" },
        geometry: {
          type: "LineString",
          coordinates: [fromLocalM(top.local, area), fromLocalM(rotate.local, area)],
        },
      });
    }
    for (const { handle, local } of handles) {
      features.push({
        type: "Feature",
        properties: { role: handle === "rotate" ? "rotate" : "handle" },
        geometry: { type: "Point", coordinates: fromLocalM(local, area) },
      });
    }
  }
  return features;
}

function pushLine(out: LngLat[][], coords: number[][]): void {
  const line: LngLat[] = [];
  for (const coord of coords) {
    if (!coord || coord.length < 2) continue;
    if (!Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) continue;
    line.push([coord[0], coord[1]]);
  }
  if (line.length >= 2) out.push(line);
}

function pushRoad(out: Array<[LngLat, LngLat]>, coords: number[][]): void {
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1];
    const b = coords[i];
    if (!a || !b || a.length < 2 || b.length < 2) continue;
    if (!Number.isFinite(a[0]) || !Number.isFinite(a[1])) continue;
    if (!Number.isFinite(b[0]) || !Number.isFinite(b[1])) continue;
    if (a[0] === b[0] && a[1] === b[1]) continue;
    out.push([
      [a[0], a[1]],
      [b[0], b[1]],
    ]);
  }
}
